import { DEFAULT_ON_FEATURES, AI_PACKS, DomainError, IMPORTANT_FACTS, type FuseTable, type TimingSheet, vehicleEngineCodes, vehicleEngineLabel, vehicleModelName, SMS_PRICE_HUF, TERMS_DOCUMENTS, TERMS_VERSION, featureAvailable, featureOn, type Plan, MIN_PASSWORD_LENGTH, partSearchKey, validateArticle, type ArticleInput, type ArticleNumber, type LegacyArticle, type LegacyWorkOrderExtra, buildLine, cleanRequiredChoices, isRole, planLegacyWorkOrders, plateSearchKey, type RequiredFieldChoices, type Row, type WorkOrderInput, type WorkOrderLineInput, type WorkOrderStatus, type CustomerInput, type ImportPlan, type Role, type VehicleInput, cleanChannels, reminderSettings, reminderSendDate, todayIso, addDays, type ReminderChannel, type ReminderKind, feature, addMonths, DEFAULT_PRICING, EMPTY_SUBSCRIPTION, effectivePlan, choiceEndsTrial, type TrialUsage, newWorkshopTerms, readOnly, parsePricing, extendedTrialUntil, TRIAL_EXTEND_MAX_DAYS, type Pricing, type SubscriptionState, AI_GUARD_DEFAULTS, inTrial, logoPath, newLogoVersion, LOGO_MAX_BYTES, type WorkshopLogo, CUSTOM_ADJUSTMENTS_PER_YEAR, customAdjustmentsUsed, customRequestRefusal, type CustomRequestStatus, siteAddressOf, MECHANIC_ROLES, isMyWorkOrder, roleOf, roleCan, hasPermission, type Action, CREDIT_DEFAULTS, type CreditSettings, creditCost, creditPurchaseRefusal, hufToCredits, rewardEarned, questionItem, TOP_QUESTION_SEED } from "@szerviz/core";
import type { PreparedLogo, IntakeLink, IntakeObjection, IntakeObjectionNotice, NewSignature, PublicIntake, WorkOrderSignature } from "./types";
import { cleanIntakeExtras, cleanObjection, intakePending, sameIntakeExtras, type IntakeExtras } from "@szerviz/core";
import { requestStepUp } from "../stepUp";
import { deviceLabel, deviceSecret, dropDeviceSecret, forgetDevice, MFA_TRUST_DAYS, saveDeviceSecret } from "../mfaTrust";
import type { TrustedDevice, ReminderDatesInput, MfaEnrollment, MfaRequirement, MfaStatus, ExportBundle, Article, ArticleNumberRow, NewStockMovement, StockMovement, Customer, DataStore, ImportProgress, ImportResult, ItemSuggestion, Member, NewPhoto, Photo, SessionUser, UserPrefs, Vehicle, WorkOrder, WorkOrderDetail, WorkOrderEvent, WorkOrderImportResult, WorkOrderItem, Workshop, WorkOrderFilter, SignedFilter, PublicQuote, PublicReady, ReadyNotice, QuoteNotice, QuoteRequest, QuoteChannel, QuoteMessage, OpBilling, OpLogEntry, OpWorkshop, AiSettings, OpAiUsage, OpAiToday, OpAiLimit, VehicleFact, VehiclePhotoResult, VehicleTypeNode, VehicleTypeSuggestion, VehicleTypeMatch, ServiceHistoryOrder, OpActivity, ContractDetails, OpContract, Invoice, CancelledInvoice, PaymentMethod, Recommendation, Proposal, ProposalInput, OpStatFigures, OpStatsOverview, OpStatsWorkshop, OpStatsMonth, OpStatTotals, CompanyCandidate, TaxpayerData, UpcomingReminder, SentReminder, OpBillingPatch, OpBulkAction, OpMember, FeedbackComment, FeedbackInput, FeedbackReport, MySubscription, OpSubscription, OpSubscriptionPatch, SubscriptionPatch, MyCustomRequests, NewCustomRequest, OpCustomRequest, OpTrialActivity, AiCredits, OpAiCredits, OpCreditPurchase, TopQuestion } from "./types";
import { applyVerification, cleanCandidates, completeTaxNumber, isCompanyName, taxBase } from "../../../../supabase/functions/_shared/taxpayer";
import { workshopContactsOf } from "../../../../supabase/functions/_shared/messaging";
import { cleanReply, cleanReport, commentText, feedbackStatus, replyComment, roleHu, LABEL_CHECK, verdictChanges, type FeedbackKind, type FeedbackVerdict } from "../../../../supabase/functions/_shared/feedback";
import { DEMO_FUSE_TABLE } from "./demoFuseTable";
import { DEMO_TIMING_SHEET } from "./demoTimingSheet";

/** The simulated authenticator app: the demo and the tests accept this code (no real TOTP). */
export const DEMO_TOTP_CODE = "123456";
const DEMO_TOTP_SECRET = "GARAGEVODEMOKEY2";
const BACKUP_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
const cleanBackup = (c: string) => c.toUpperCase().replace(/[^0-9A-Z]/g, "");

let seq = 0;
const newId = () => `demo-${++seq}-${Math.random().toString(36).slice(2, 8)}`;
const now = () => new Date().toISOString();
const contains = (hay: string | null | undefined, needle: string) => (hay ?? "").toLowerCase().includes(needle);

/** In-memory store: demo mode without a cloud connection, and the user interface tests. */
export class MemoryStore implements DataStore {
  readonly mode = "demo" as const;
  readonly allowSignUp: boolean;
  /** Package of new workshops; the demo shows everything (premium). */
  private readonly plan: Plan;
  /** Whether new workshops must accept the legal documents (off in the demo and most tests: they start accepted). */
  private readonly requireTerms: boolean;
  /** E-mail addresses of Garagevo operators (operator console). */
  private readonly operators: string[];
  /** Whether two-step sign in is required of owners and operators (off in the demo and most tests). */
  private readonly mfaRequired: boolean;
  /** Length of the trial of new workshops (days; 0: none, as in most tests; the first 7 days Profi). */
  private readonly trialDays: number;
  constructor(options: { allowSignUp?: boolean; plan?: Plan; requireTerms?: boolean; operators?: string[]; mfa?: boolean; trialDays?: number; confirmEmail?: boolean } = {}) {
    this.trialDays = options.trialDays ?? 0;
    this.confirmEmail = options.confirmEmail ?? false;
    this.operators = options.operators ?? [];
    this.mfaRequired = options.mfa ?? false;
    this.allowSignUp = options.allowSignUp ?? true;
    this.plan = options.plan ?? "premium";
    this.requireTerms = options.requireTerms ?? false;
  }
  private terms: { workshopId: string; userId: string; document: string; version: string; acceptedAt: string }[] = [];
  private user: SessionUser | null = null;
  private members = new Map<string, Member[]>();
  /** Password reset requests, visible to tests. */
  readonly resetRequests: string[] = [];
  private listeners = new Set<() => void>();
  private workshops: Workshop[] = [];
  /** When each workshop was created (operator statistics). */
  workshopCreated = new Map<string, string>();
  private customers = new Map<string, Customer>();
  private vehicles = new Map<string, Vehicle>();

  async currentUser() {
    return this.user;
  }
  onAuthChange(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  private emit() {
    for (const l of this.listeners) l();
  }
  /** Last sign in of each user (operator statistics). */
  signIns = new Map<string, string>();
  /** Sign up needs a confirmed e-mail address (as Supabase Auth "Confirm email"); tests confirm with confirmEmailAddress. */
  private confirmEmail: boolean;
  private unconfirmed = new Set<string>();
  /** Test helper: the link in the confirmation e-mail was clicked. */
  confirmEmailAddress(email: string) {
    this.unconfirmed.delete(email.toLowerCase());
  }
  async signIn(email: string) {
    if (this.unconfirmed.has(email.toLowerCase())) throw new DomainError("EMAIL_NOT_CONFIRMED");
    this.user = { id: `demo-user-${email.toLowerCase()}`, email: email.toLowerCase() };
    this.aal = "aal1";
    this.signIns.set(this.user.id, now());
    this.emit();
  }
  async signUp(email: string) {
    if (this.confirmEmail) {
      this.unconfirmed.add(email.toLowerCase());
      return { needsConfirmation: true };
    }
    await this.signIn(email);
    return { needsConfirmation: false };
  }
  async signOut(options: { forgetDevice?: boolean } = {}) {
    if (options.forgetDevice && this.user) {
      const s = deviceSecret(this.user.id);
      const d = s ? this.trusted.get(s) : undefined;
      if (d) d.revokedAt = now();
      forgetDevice(this.user.id);
    }
    this.user = null;
    this.aal = "aal1";
    this.emit();
  }

  // ---- two-step sign in (simulated authenticator: DEMO_TOTP_CODE) ----
  /** Assurance level of the current session. */
  private aal: "aal1" | "aal2" = "aal1";
  /** Users with a confirmed authenticator. */
  private mfaFactors = new Set<string>();
  private mfaPending = new Map<string, string>();
  /** Unused backup codes of each user (kept in memory only; the cloud keeps hashes). */
  private backupCodes = new Map<string, string[]>();
  private recoveryFails = new Map<string, number[]>();
  /** Notice e-mails the operator's reset "sent" (tests). */
  readonly mfaResetMails: { to: string[]; email: string; workshop: string }[] = [];
  private signedIn(): SessionUser {
    if (!this.user) throw new DomainError("NOT_ALLOWED");
    return this.user;
  }
  /**
   * Like public.mfa_assert(): a user with an authenticator, and (with the mfa option) a required user
   * without one, must have passed the second step. The cloud checks every workshop read and write; the
   * demo only its sensitive paths.
   */
  private mfaAssert() {
    const u = this.user;
    if (!u || this.aal === "aal2" || this.deviceTrusted()) return;
    if (this.mfaFactors.has(u.id)) throw new DomainError("MFA_REQUIRED");
    if (this.mfaRequired && this.mfaRequiredNow(u)) throw new DomainError("MFA_SETUP_REQUIRED");
  }
  /** Remembered devices by secret (the cloud keeps only hashes), like public.mfa_trusted_devices. */
  private trusted = new Map<string, { id: string; userId: string; label: string; createdAt: string; lastUsedAt: string; expiresAt: string; revokedAt: string | null }>();
  /** Like public.mfa_device_trusted(): this browser's secret is a valid record of the signed in user. */
  private deviceTrusted() {
    const u = this.user;
    // the operator too, for his own workshop; opAssert asks the real code
    if (!u || !this.mfaFactors.has(u.id)) return false;
    const s = deviceSecret(u.id);
    const d = s ? this.trusted.get(s) : undefined;
    return !!d && d.userId === u.id && !d.revokedAt && Date.parse(d.expiresAt) > Date.now();
  }
  private revokeDevices(userId: string) {
    for (const d of this.trusted.values()) if (d.userId === userId && !d.revokedAt) d.revokedAt = now();
  }
  /**
   * Test helper: the storage service does not see the remembered device (the fallback of
   * SupabaseStore.upload): an upload at aal1 on a remembered device asks the code first.
   */
  storageIgnoresDevice = false;
  private async storageStepUp() {
    const u = this.user;
    if (!this.storageIgnoresDevice || !u || this.aal === "aal2" || !this.mfaFactors.has(u.id)) return;
    if (!(await requestStepUp())) throw new DomainError("MFA_REQUIRED");
  }
  /** Test helper: moves a remembered device's expiry (e.g. 31 days later). */
  expireDevices(userId: string) {
    for (const d of this.trusted.values()) if (d.userId === userId) d.expiresAt = new Date(Date.now() - 1000).toISOString();
  }
  async mfaTrustDevice() {
    const u = this.signedIn();
    if (this.aal !== "aal2" || !this.mfaFactors.has(u.id)) throw new DomainError("MFA_REQUIRED");
    const bytes = new Uint8Array(32);
    globalThis.crypto.getRandomValues(bytes);
    const secret = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    const at = now();
    this.trusted.set(secret, { id: newId(), userId: u.id, label: deviceLabel(), createdAt: at, lastUsedAt: at, expiresAt: new Date(Date.now() + MFA_TRUST_DAYS * 864e5).toISOString(), revokedAt: null });
    saveDeviceSecret(u.id, secret);
  }
  async mfaDeviceOk() {
    const u = this.user;
    if (!u || !deviceSecret(u.id)) return false;
    const ok = this.deviceTrusted();
    if (!ok) dropDeviceSecret(u.id);
    return ok;
  }
  async mfaDevices(): Promise<TrustedDevice[]> {
    const u = this.signedIn();
    const mine = deviceSecret(u.id);
    return [...this.trusted.entries()]
      .filter(([, d]) => d.userId === u.id && !d.revokedAt && Date.parse(d.expiresAt) > Date.now())
      .map(([s, d]) => ({ id: d.id, label: d.label, createdAt: d.createdAt, lastUsedAt: d.lastUsedAt, expiresAt: d.expiresAt, current: s === mine }));
  }
  async mfaForgetDevice(device: TrustedDevice) {
    const u = this.signedIn();
    for (const d of this.trusted.values()) if (d.id === device.id && d.userId === u.id) d.revokedAt = now();
    if (device.current) dropDeviceSecret(u.id);
  }
  async mfaForgetAllDevices() {
    const u = this.signedIn();
    this.revokeDevices(u.id);
    dropDeviceSecret(u.id);
  }
  private mfaRequiredNow(u: SessionUser) {
    if (this.operators.includes(u.email)) return true;
    return this.workshops.some((w) => {
      const role = (this.members.get(w.id) ?? []).find((m) => m.userId === u.id)?.role;
      return (role === "owner" || (role === "manager" && w.general.mfaManagers === true)) && !this.isTestWorkshop(w.id);
    });
  }
  private isTestWorkshop(workshopId: string) {
    return (this.extras.get(workshopId)?.tags ?? []).some((x) => x.trim().toLowerCase() === "teszt");
  }
  async mfaStatus(): Promise<MfaStatus> {
    const u = this.user;
    return { factor: !!u && this.mfaFactors.has(u.id), verified: !!u && this.aal === "aal2" };
  }
  async mfaRequirement(): Promise<MfaRequirement> {
    const u = this.user;
    if (!u || !this.mfaRequired) return { required: false, reason: null, testExempt: false };
    if (await this.isPlatformAdmin()) return { required: true, reason: "operator", testExempt: false };
    const mine = this.workshops
      .map((w) => ({ w, role: (this.members.get(w.id) ?? []).find((m) => m.userId === u.id)?.role }))
      .filter(({ w, role }) => role === "owner" || (role === "manager" && w.general.mfaManagers === true));
    const real = mine.filter(({ w }) => !this.isTestWorkshop(w.id));
    if (real.length > 0) return { required: true, reason: real.some((x) => x.role === "owner") ? "owner" : "manager", testExempt: false };
    return { required: false, reason: null, testExempt: mine.length > 0 };
  }
  async mfaEnroll(): Promise<MfaEnrollment> {
    const u = this.signedIn();
    const factorId = newId();
    this.mfaPending.set(u.id, factorId);
    return { factorId, secret: DEMO_TOTP_SECRET, uri: `otpauth://totp/Garagevo:${encodeURIComponent(u.email)}?secret=${DEMO_TOTP_SECRET}&issuer=Garagevo` };
  }
  async mfaConfirmEnroll(factorId: string, code: string) {
    const u = this.signedIn();
    if (this.mfaPending.get(u.id) !== factorId) throw new DomainError("MFA_FAILED");
    if (code.trim() !== DEMO_TOTP_CODE) throw new DomainError("MFA_CODE_INVALID");
    this.mfaPending.delete(u.id);
    this.mfaFactors.add(u.id);
    this.aal = "aal2";
    return this.mfaNewBackupCodes();
  }
  async mfaVerify(code: string) {
    const u = this.signedIn();
    if (!this.mfaFactors.has(u.id)) throw new DomainError("MFA_FAILED");
    if (code.trim() !== DEMO_TOTP_CODE) throw new DomainError("MFA_CODE_INVALID");
    this.aal = "aal2";
  }
  async mfaRecover(code: string) {
    const u = this.signedIn();
    const hour = Date.now() - 3600e3;
    const fails = (this.recoveryFails.get(u.id) ?? []).filter((x) => x > hour);
    if (fails.length >= 10) throw new DomainError("MFA_TOO_MANY");
    const codes = this.backupCodes.get(u.id) ?? [];
    if (!codes.includes(cleanBackup(code))) {
      this.recoveryFails.set(u.id, [...fails, Date.now()]);
      throw new DomainError("MFA_CODE_INVALID");
    }
    this.backupCodes.delete(u.id);
    this.mfaFactors.delete(u.id);
    this.revokeDevices(u.id);
    forgetDevice(u.id);
    await this.signOut();
  }
  async mfaNewBackupCodes() {
    const u = this.signedIn();
    if (!this.mfaFactors.has(u.id) || this.aal !== "aal2") throw new DomainError("MFA_REQUIRED");
    const random = new Uint8Array(100);
    globalThis.crypto.getRandomValues(random);
    const codes = Array.from({ length: 10 }, (_, i) => Array.from(random.subarray(i * 10, i * 10 + 10), (b) => BACKUP_ALPHABET[b % 32]).join(""));
    this.backupCodes.set(u.id, codes);
    return codes.map((c) => `${c.slice(0, 5)}-${c.slice(5)}`);
  }
  async mfaBackupCodesLeft() {
    return this.user ? (this.backupCodes.get(this.user.id) ?? []).length : 0;
  }
  async mfaDisable() {
    const u = this.signedIn();
    // like Supabase Auth: removing the authenticator needs the code in this session (aal2)
    if (this.mfaFactors.has(u.id) && this.aal !== "aal2") throw new DomainError("MFA_REQUIRED");
    this.mfaAssert();
    this.mfaFactors.delete(u.id);
    this.backupCodes.delete(u.id);
    this.revokeDevices(u.id);
    forgetDevice(u.id);
  }
  async opResetMfa(workshopId: string, userId: string) {
    await this.opAssert();
    const m = (this.members.get(workshopId) ?? []).find((x) => x.userId === userId);
    if (!m) throw new DomainError("NOT_ALLOWED");
    const factors = this.mfaFactors.delete(userId) ? 1 : 0;
    this.backupCodes.delete(userId);
    this.revokeDevices(userId);
    // demo: no e-mail goes out, the notice is only recorded
    const owners = (this.members.get(workshopId) ?? []).filter((x) => x.role === "owner").map((x) => x.email);
    this.mfaResetMails.push({ to: owners, email: m.email, workshop: this.workshops.find((w) => w.id === workshopId)?.name ?? "" });
    await this.opAssert(workshopId, "mfaReset", { email: m.email, factors, mailed: false });
    return { mailed: false };
  }

  async requestPasswordReset(email: string) {
    this.resetRequests.push(email);
  }
  async setPassword(password: string) {
    if (password.length < MIN_PASSWORD_LENGTH) throw new DomainError("PASSWORD_TOO_SHORT");
    // a new password forgets every remembered device
    if (this.user) {
      this.revokeDevices(this.user.id);
      dropDeviceSecret(this.user.id);
    }
  }

  private prefs = new Map<string, UserPrefs>();
  async getPrefs() {
    return { ...(this.prefs.get(this.user?.id ?? "") ?? { helpEnabled: true, helpSince: null, helpAsked: false }) };
  }
  async savePrefs(patch: Partial<UserPrefs>) {
    const next = { ...(await this.getPrefs()), ...patch };
    if (this.user) this.prefs.set(this.user.id, next);
    return next;
  }
  async saveRequiredFields(workshopId: string, choices: RequiredFieldChoices) {
    // the workshop's settings are the owner's (2026-11-04)
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_ALLOWED");
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) w.requiredFields = cleanRequiredChoices(choices);
  }

  async myWorkshops() {
    const u = this.user;
    if (!u) return [];
    for (const w of this.workshops) this.refreshTrial(w);
    return this.workshops.filter((w) => (this.members.get(w.id) ?? []).some((m) => m.userId === u.id)).map((w) => ({ ...w, requiredFields: { ...w.requiredFields }, general: { ...w.general }, permissions: { ...w.permissions }, features: [...w.features], extraFeatures: [...(w.extraFeatures ?? [])], logo: w.logo ? { ...w.logo } : null }));
  }
  async createWorkshop(name: string, language: string) {
    const w: Workshop = { id: newId(), name, language, currency: "HUF", requiredFields: {}, general: {}, permissions: {}, plan: this.plan, features: [...DEFAULT_ON_FEATURES] };
    this.workshops.push(w);
    // the subscription: trial (Premium meanwhile) and the founder offer, like the database trigger
    const pricing = { ...this.pricing, trialDays: this.trialDays };
    const founder = [...this.subs.values()].filter((x) => x.founder).length < pricing.founder.limit;
    this.subs.set(w.id, { ...EMPTY_SUBSCRIPTION, plan: this.plan, ...newWorkshopTerms(pricing, todayIso(), founder) });
    this.refreshTrial(w);
    this.workshopCreated.set(w.id, now());
    if (this.user) this.members.set(w.id, [{ userId: this.user.id, email: this.user.email, role: "owner" }]);
    if (!this.requireTerms && this.user) await this.acceptTerms(w.id, TERMS_VERSION);
    return w;
  }

  async termsAcceptances(workshopId: string) {
    if (!this.myRole(workshopId)) return [];
    return this.terms.filter((a) => a.workshopId === workshopId).map(({ document, version, acceptedAt }) => ({ document, version, acceptedAt }));
  }
  /** Concluded contracts and the confirmation e-mails "sent" (tests). */
  contracts: (OpContract & { workshopId: string })[] = [];
  async acceptTerms(workshopId: string, version: string, details?: ContractDetails) {
    const u = this.user;
    if (!u || !this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    const at = new Date().toISOString();
    if (details) {
      if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_OWNER");
      if (!details.companyName.trim() || !details.address.trim() || !details.taxNumber.trim()) throw new DomainError("REQUIRED_MISSING");
      this.contracts.push({ workshopId, ...details, regNumber: details.regNumber?.trim() || null, version, userEmail: u.email, acceptedAt: at, confirmationSentAt: at });
      // like accept_terms: the contract's company is the legal name where none is given yet
      const w = this.workshops.find((x) => x.id === workshopId);
      if (w && !String(w.general.legalName ?? "").trim()) w.general = { ...w.general, legalName: details.companyName.trim() };
    }
    for (const document of TERMS_DOCUMENTS) this.terms.push({ workshopId, userId: u.id, document, version, acceptedAt: at });
  }
  async opExtendTrial(workshopId: string, days: number): Promise<string> {
    await this.opAssert();
    const sub = this.subOf(workshopId);
    const today = todayIso();
    const ro = readOnly({ ...sub, status: this.billingOf(workshopId).status }, today);
    if (!Number.isInteger(days) || days < 1 || days > TRIAL_EXTEND_MAX_DAYS || !sub.trialUntil || !(sub.trialUntil >= today || ro)) throw new DomainError("NOT_ALLOWED");
    const from = sub.trialUntil;
    sub.trialUntil = extendedTrialUntil(sub.trialUntil, today, days);
    sub.billingAnchor = addDays(sub.trialUntil, 1);
    if (sub.founder) Object.assign(sub, { founderUntil: addDays(addMonths(sub.billingAnchor, this.pricing.founder.months), -1), priceLockUntil: addDays(addMonths(sub.billingAnchor, this.pricing.founder.lockMonths), -1) });
    await this.opAssert(workshopId, "trialExtend", { days, from, to: sub.trialUntil });
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) this.refreshTrial(w);
    return sub.trialUntil;
  }
  async opTrialActivity(workshopId: string): Promise<OpTrialActivity> {
    await this.opAssert();
    const since = new Date(Date.now() - 7 * 86400000).toISOString();
    const members = this.members.get(workshopId) ?? [];
    const orders = [...this.orders.values()].filter((o) => o.workshopId === workshopId);
    const signIns = members.map((m) => this.signIns.get(m.userId) ?? "").filter(Boolean).sort();
    return {
      workOrders: orders.filter((o) => o.receivedAt >= since).length,
      vehicles: [...this.vehicles.values()].filter((v) => v.workshopId === workshopId && v.createdAt >= since).length,
      signedIn: signIns.filter((s) => s >= since).length,
      lastActivity: orders.map((o) => o.updatedAt ?? o.receivedAt).sort().at(-1) || null,
      lastSignIn: signIns.at(-1) ?? null,
    };
  }
  /** Custom adjustments recorded by the operator (public.custom_requests). */
  customRequests: (OpCustomRequest & { workshopId: string })[] = [];
  async myCustomRequests(workshopId: string): Promise<MyCustomRequests> {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_OWNER");
    this.mfaAssert();
    const year = Number(todayIso().slice(0, 4));
    const plan = this.workshops.find((w) => w.id === workshopId)?.plan ?? "basic";
    const list = this.customRequests.filter((r) => r.workshopId === workshopId);
    return {
      year, used: customAdjustmentsUsed(list, year), included: CUSTOM_ADJUSTMENTS_PER_YEAR[plan],
      requests: list.filter((r) => r.year === year).map(({ workshopId: _w, createdByEmail: _e, ...r }) => r).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    };
  }
  async opCustomRequests(workshopId: string) {
    await this.opAssert();
    return this.customRequests.filter((r) => r.workshopId === workshopId).map(({ workshopId: _w, ...r }) => r)
      .sort((a, b) => b.year - a.year || b.createdAt.localeCompare(a.createdAt));
  }
  async opAddCustomRequest(workshopId: string, r: NewCustomRequest) {
    await this.opAssert();
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w) throw new DomainError("NOT_ALLOWED");
    const refusal = customRequestRefusal(r, customAdjustmentsUsed(this.customRequests.filter((x) => x.workshopId === workshopId), r.year), w.plan);
    if (refusal) throw new DomainError(refusal);
    const id = newId();
    this.customRequests.push({ id, workshopId, year: r.year, title: r.title.trim(), hours: r.hours, included: r.included, status: "open", createdAt: new Date().toISOString(), createdByEmail: this.user!.email });
    await this.opAssert(workshopId, "customRequest", { id, year: r.year, title: r.title.trim(), hours: r.hours, included: r.included });
  }
  async opUpdateCustomRequest(id: string, status: CustomRequestStatus, hours: number | null = null) {
    await this.opAssert();
    const r = this.customRequests.find((x) => x.id === id);
    if (!r || !["open", "done", "cancelled"].includes(status)) throw new DomainError("NOT_ALLOWED");
    const plan = this.workshops.find((w) => w.id === r.workshopId)?.plan ?? "basic";
    if (r.status === "cancelled" && status !== "cancelled" && r.included
        && customAdjustmentsUsed(this.customRequests.filter((x) => x.workshopId === r.workshopId), r.year) >= CUSTOM_ADJUSTMENTS_PER_YEAR[plan]) throw new DomainError("CUSTOM_LIMIT");
    const h = hours ?? r.hours;
    if (h <= 0 || h > 1000 || (r.included && h > 4)) throw new DomainError("NOT_ALLOWED");
    Object.assign(r, { status, hours: h });
    await this.opAssert(r.workshopId, "customRequestStatus", { id, status, hours: h });
  }
  async opContracts(workshopId: string) {
    await this.opAssert();
    return this.contracts.filter((c) => c.workshopId === workshopId).map(({ workshopId: _w, ...c }) => c);
  }
  async exportAll(workshopId: string): Promise<ExportBundle> {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    const mine = <T extends { workshopId?: string }>(list: Iterable<T>) => [...list].filter((r) => r.workshopId === workshopId) as unknown as Record<string, unknown>[];
    const orders = mine(this.orders.values());
    const orderIds = new Set(orders.map((o) => o.id));
    const photos = [...this.photos.values()].filter((p) => p.workshopId === workshopId);
    return {
      tables: {
        workshops: mine(this.workshops.map((w) => ({ ...w, workshopId: w.id }))),
        workshop_members: [...(this.members.get(workshopId) ?? [])] as unknown as Record<string, unknown>[],
        customers: mine(this.customers.values()),
        vehicles: mine(this.vehicles.values()),
        work_orders: orders,
        work_order_items: [...this.items.values()].filter((i) => orderIds.has(i.workOrderId)) as unknown as Record<string, unknown>[],
        quote_requests: this.quotes.filter((q) => orderIds.has(q.workOrderId)) as unknown as Record<string, unknown>[],
        articles: mine(this.articles.values()),
        stock_movements: mine(this.movements),
        photos: photos.map(({ url: _url, ...p }) => p) as unknown as Record<string, unknown>[],
        terms_acceptances: mine(this.terms),
      },
      photos: photos.map((p) => ({ name: `photos/${p.id}.jpg`, load: async () => this.photoBlobs.get(p.id) ?? new Blob([]) })),
    };
  }

  private myRole(workshopId: string): Role | null {
    return roleOf((this.members.get(workshopId) ?? []).find((m) => m.userId === this.user?.id)?.role);
  }
  /** Like public.can_do(). */
  private can(workshopId: string, action: Action): boolean {
    const w = this.workshops.find((x) => x.id === workshopId);
    return roleCan(this.myRole(workshopId), w?.permissions ?? {}, action);
  }
  private requireCan(workshopId: string, action: Action) {
    if (!this.can(workshopId, action)) throw new DomainError("NOT_ALLOWED");
  }
  /** What a member without the prices right gets: the line without its money. */
  private priced(workshopId: string, i: WorkOrderItem): WorkOrderItem {
    return this.can(workshopId, "prices") ? { ...i } : { ...i, unitPrice: 0, net: 0, vat: 0, gross: 0 };
  }
  private requireOwner(workshopId: string) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_OWNER");
  }
  async listMembers(workshopId: string) {
    return this.myRole(workshopId) ? [...(this.members.get(workshopId) ?? [])] : [];
  }
  async inviteMember(workshopId: string, email: string, role: Role) {
    this.requireOwner(workshopId);
    this.mfaAssert();
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) throw new DomainError("INVALID_EMAIL", e);
    if (!isRole(role)) throw new DomainError("INVALID_ROLE", role);
    const list = this.members.get(workshopId) ?? [];
    const existing = list.find((m) => m.email === e);
    if (!existing && list.length >= this.seatLimit(workshopId)) throw new DomainError("SEATS_FULL");
    if (existing) existing.role = role;
    else list.push({ userId: `demo-user-${e}`, email: e, role });
    this.members.set(workshopId, list);
    return existing ? ("added" as const) : ("invited" as const);
  }
  private wouldLeaveNoOwner(workshopId: string, userId: string) {
    const list = this.members.get(workshopId) ?? [];
    return !list.some((m) => m.role === "owner" && m.userId !== userId);
  }
  async updateMemberRole(workshopId: string, userId: string, role: Role) {
    this.requireOwner(workshopId);
    const m = (this.members.get(workshopId) ?? []).find((x) => x.userId === userId);
    if (!m) return;
    if (m.role === "owner" && role !== "owner" && this.wouldLeaveNoOwner(workshopId, userId)) throw new DomainError("LAST_OWNER");
    m.role = role;
  }
  async removeMember(workshopId: string, userId: string) {
    this.requireOwner(workshopId);
    const list = this.members.get(workshopId) ?? [];
    const m = list.find((x) => x.userId === userId);
    if (m?.role === "owner" && this.wouldLeaveNoOwner(workshopId, userId)) throw new DomainError("LAST_OWNER");
    this.members.set(workshopId, list.filter((x) => x.userId !== userId));
  }

  async setMemberName(workshopId: string, userId: string, name: string) {
    const me = this.myRole(workshopId);
    if (!me || (me !== "owner" && userId !== this.user?.id)) throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    const m = (this.members.get(workshopId) ?? []).find((x) => x.userId === userId);
    if (!m) throw new DomainError("NOT_ALLOWED");
    m.displayName = name.trim().replace(/\s+/g, " ").slice(0, 80) || null;
  }

  async searchCustomers(workshopId: string, query: string, limit = 100) {
    const q = query.trim().toLowerCase();
    // the owners of matching plates too, like the database store (#121)
    const plate = plateSearchKey(query);
    const owners = new Set(
      plate.length >= 3 ? [...this.vehicles.values()].filter((v) => v.workshopId === workshopId && v.customerId && plateSearchKey(v.plateNumber).includes(plate)).map((v) => v.customerId) : [],
    );
    return [...this.customers.values()]
      .filter((c) => c.workshopId === workshopId)
      .filter((c) => !q || contains(c.name, q) || contains(c.phone, q) || contains(c.mobile, q) || contains(c.email, q) || contains(c.city, q) || owners.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name, "hu"))
      .slice(0, limit);
  }
  async getVehicle(id: string) {
    const v = this.vehicles.get(id);
    return v ? { ...v, customerName: v.customerId ? (this.customers.get(v.customerId)?.name ?? null) : null } : null;
  }
  async getCustomer(id: string) {
    return this.customers.get(id) ?? null;
  }
  async saveCustomer(workshopId: string, input: CustomerInput, id?: string) {
    this.assertWritable(workshopId);
    const existing = id ? this.customers.get(id) : undefined;
    const c: Customer = { ...existing, ...input, id: existing?.id ?? newId(), workshopId, createdAt: existing?.createdAt ?? now() };
    // like the database: the consent time is set when the first channel is ticked, cleared with the last
    c.reminderChannels = cleanChannels(c.reminderChannels);
    const hadConsent = !!existing?.reminderConsentAt && (existing.reminderChannels ?? []).length > 0;
    c.reminderConsentAt = c.reminderChannels.length === 0 ? null : hadConsent ? existing!.reminderConsentAt! : now();
    this.customers.set(c.id, c);
    return c;
  }
  async deleteCustomer(id: string) {
    const cu = this.customers.get(id);
    if (cu) this.requireCan(cu.workshopId, "customerDelete");
    if ([...this.orders.values()].some((o) => o.customerId === id)) throw new DomainError("CUSTOMER_HAS_WORK_ORDERS");
    this.customers.delete(id);
    for (const v of this.vehicles.values()) if (v.customerId === id) v.customerId = null;
  }

  private withOwner(v: Vehicle): Vehicle {
    return { ...v, customerName: v.customerId ? (this.customers.get(v.customerId)?.name ?? null) : null };
  }
  async searchVehicles(workshopId: string, query: string, limit = 100) {
    const q = query.trim().toLowerCase();
    const plate = plateSearchKey(query);
    return [...this.vehicles.values()]
      .filter((v) => v.workshopId === workshopId)
      .map((v) => this.withOwner(v))
      .filter((v) => !q || (plate && plateSearchKey(v.plateNumber).includes(plate)) || contains(v.make, q) || contains(v.model, q) || contains(v.vin, q) || contains(v.customerName, q))
      .sort((a, b) => a.plateNumber.localeCompare(b.plateNumber))
      .slice(0, limit);
  }
  async vehiclesOfCustomer(customerId: string) {
    return [...this.vehicles.values()].filter((v) => v.customerId === customerId).map((v) => this.withOwner(v));
  }
  async saveVehicle(workshopId: string, input: VehicleInput, id?: string) {
    this.assertWritable(workshopId);
    const existing = id ? this.vehicles.get(id) : undefined;
    const v: Vehicle = { ...existing, ...input, id: existing?.id ?? newId(), workshopId, createdAt: existing?.createdAt ?? now() };
    this.vehicles.set(v.id, v);
    return this.withOwner(v);
  }
  async deleteVehicle(id: string) {
    if ([...this.orders.values()].some((o) => o.vehicleId === id)) throw new DomainError("VEHICLE_HAS_WORK_ORDERS");
    this.vehicles.delete(id);
  }

  async importLegacy(workshopId: string, plan: ImportPlan, onProgress?: (p: ImportProgress) => void): Promise<ImportResult> {
    const byLegacy = new Map<string, string>();
    for (const c of this.customers.values()) if (c.workshopId === workshopId && c.legacyId) byLegacy.set(c.legacyId, c.id);
    let done = 0;
    for (const input of plan.customers) {
      const saved = await this.saveCustomer(workshopId, input, byLegacy.get(input.legacyId ?? ""));
      if (input.legacyId) byLegacy.set(input.legacyId, saved.id);
      onProgress?.({ phase: "customers", done: ++done, total: plan.customers.length });
    }
    const vehicleByLegacy = new Map<string, string>();
    for (const v of this.vehicles.values()) if (v.workshopId === workshopId && v.legacyId) vehicleByLegacy.set(v.legacyId, v.id);
    let withoutOwner = 0;
    done = 0;
    for (const { customerLegacyId, ...input } of plan.vehicles) {
      const customerId = customerLegacyId ? (byLegacy.get(customerLegacyId) ?? null) : null;
      if (!customerId) withoutOwner++;
      await this.saveVehicle(workshopId, { ...input, customerId }, vehicleByLegacy.get(input.legacyId ?? ""));
      onProgress?.({ phase: "vehicles", done: ++done, total: plan.vehicles.length });
    }
    onProgress?.({ phase: "done", done: 0, total: 0 });
    return { customers: plan.customers.length, vehicles: plan.vehicles.length, vehiclesWithoutOwner: withoutOwner };
  }

  // ---- settings beyond required fields ----
  async renameWorkshop(workshopId: string, name: string) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_ALLOWED");
    const n = name.replace(/\s+/g, " ").trim();
    if (!n) throw new DomainError("REQUIRED_MISSING");
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) w.name = n;
  }
  async saveGeneralSettings(workshopId: string, values: Record<string, number | string | boolean | null>) {
    const role = this.myRole(workshopId);
    if (role !== "owner") throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    const w = this.workshops.find((x) => x.id === workshopId);
    // only the owner decides whether managers must use two-step sign in
    if (w && (values.mfaManagers ?? null) !== (w.general.mfaManagers ?? null) && role !== "owner") throw new DomainError("NOT_ALLOWED");
    if (w) w.general = { ...values };
  }
  async saveFeatures(workshopId: string, features: string[]) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_ALLOWED");
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w) return;
    // like the package guard: a feature that was on may stay while the trial is not settled (it does not work meanwhile)
    if (features.some((f) => !featureAvailable(w.plan, f, w.extraFeatures) && !w.features.includes(f))) throw new DomainError("FEATURE_NOT_IN_PLAN");
    const keep = this.trialUnsettled(w.id);
    w.features = [...new Set(features)].filter((f) => featureAvailable(w.plan, f, w.extraFeatures) || (keep && w.features.includes(f)));
  }
  async savePermissions(workshopId: string, permissions: Record<string, Role[]>) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) w.permissions = { ...permissions };
  }
  private hasPermission(workshopId: string, permission: string): boolean {
    const w = this.workshops.find((x) => x.id === workshopId);
    return hasPermission(this.myRole(workshopId), w?.permissions ?? {}, permission);
  }

  // ---- work orders ----
  private orders = new Map<string, WorkOrder>();
  private items = new Map<string, WorkOrderItem>();
  private events = new Map<string, WorkOrderEvent[]>();
  private counters = new Map<string, number>();
  /** Change stamp of each work order header (the cloud uses updated_at). */
  private stamps = new Map<string, number>();

  /** Like the database: the extras in their clean form; setting or changing them needs the intakePhotos feature. */
  private withIntakeExtras(workshopId: string, input: WorkOrderInput, before?: IntakeExtras | null): WorkOrderInput {
    if (input.intakeExtras === undefined) return input;
    const extras = cleanIntakeExtras(input.intakeExtras);
    const w = this.workshops.find((x) => x.id === workshopId);
    if (extras && !sameIntakeExtras(extras, before) && (!w || !featureOn(w, "intakePhotos"))) throw new DomainError("FEATURE_NOT_IN_PLAN");
    return { ...input, intakeExtras: extras };
  }
  private decorate(o: WorkOrder): WorkOrder {
    const v = this.vehicles.get(o.vehicleId ?? "");
    const lines = [...this.items.values()].filter((i) => i.workOrderId === o.id);
    const gross = this.can(o.workshopId, "prices") ? lines.filter((i) => i.approval !== "declined").reduce((a, i) => a + i.gross, 0) : 0;
    return {
      ...o,
      plateNumber: v?.plateNumber ?? "",
      plateCountry: v?.plateCountry ?? null,
      vehicleLabel: v ? [v.make, v.model].filter(Boolean).join(" ") || null : null,
      customerName: this.customers.get(o.customerId ?? "")?.name ?? null,
      gross,
      itemCount: lines.length,
      lineMechanicIds: lines.filter((i) => i.kind === "labour" && i.approval !== "declined").map((i) => i.mechanicId ?? null),
      pendingApprovals: lines.filter((i) => i.approval === "pending").length,
      openObjections: this.objections.filter((x) => x.workOrderId === o.id && !x.resolvedAt).length,
      updatedAt: String(this.stamps.get(o.id) ?? 0),
      paperSigned: this.paperSignatures.get(o.id) ?? null,
      intakeSigned: this.intakeSigned(o.id),
    };
  }
  private log(o: WorkOrder, kind: WorkOrderEvent["kind"], reason: string | null = null) {
    this.events.set(o.id, [...(this.events.get(o.id) ?? []), { kind, reason, at: now() }]);
  }
  private requireOpen(id: string): WorkOrder {
    const o = this.orders.get(id);
    if (!o) throw new DomainError("NOT_ALLOWED");
    if (o.status === "closed") throw new DomainError("WORK_ORDER_CLOSED");
    return o;
  }
  private sorted(list: WorkOrder[]) {
    return list.map((o) => this.decorate(o)).sort((a, b) => b.receivedAt.localeCompare(a.receivedAt) || b.number.localeCompare(a.number));
  }

  /** "Átvétel a szerelőre bízva": the paper signature records (public.work_order_paper_signatures). */
  private paperSignatures = new Map<string, { at: string; by: string | null }>();
  /** Like public.intake_signed(): phone signature, paper record or a photo of the signed paper. */
  private intakeSigned(id: string): boolean {
    return this.paperSignatures.has(id) || this.signatureRows.some((x) => x.workOrderId === id && x.kind === "intake")
      || [...this.photos.values()].some((p) => p.workOrderId === id && p.docType === "signedIntake");
  }
  /** Like public.work_orders_intake_guard(): the odometer may wait only while the intake waits for the mechanic. */
  private assertIntake(o: { odometerKm?: number | null; legacyId?: string | null; intakeByMechanic?: boolean; intakeDoneAt?: string | null }) {
    if ((o.odometerKm === null || o.odometerKm === undefined) && !o.legacyId && !intakePending(o)) throw new DomainError("REQUIRED_MISSING");
  }
  async recordPaperSignature(workshopId: string, workOrderId: string) {
    const o = this.orders.get(workOrderId);
    if (!o || o.workshopId !== workshopId || !this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    this.assertWritable(workshopId);
    if (intakePending(o)) throw new DomainError("INTAKE_PENDING");
    if (!this.paperSignatures.has(workOrderId)) this.paperSignatures.set(workOrderId, { at: now(), by: this.user?.id ?? null });
  }
  async deletePaperSignature(workOrderId: string) {
    const o = this.orders.get(workOrderId);
    if (!o) throw new DomainError("NOT_ALLOWED");
    this.requireCan(o.workshopId, "close");
    this.paperSignatures.delete(workOrderId);
  }
  async finishIntake(id: string, input: { odometerKm: number; intakeExtras?: IntakeExtras | null }) {
    const o = this.requireOpen(id);
    this.assertWritable(o.workshopId);
    if (!intakePending(o)) throw new DomainError("NOT_ALLOWED");
    if ((this.user?.id ?? null) !== (o.mechanicId ?? null) && !this.can(o.workshopId, "close")) throw new DomainError("NOT_ALLOWED");
    if (input.odometerKm === null || input.odometerKm === undefined || input.odometerKm < 0) throw new DomainError("REQUIRED_MISSING");
    const extras = input.intakeExtras === undefined ? o.intakeExtras : this.withIntakeExtras(o.workshopId, { ...o, intakeExtras: input.intakeExtras }, o.intakeExtras).intakeExtras;
    this.orders.set(id, { ...o, intakeExtras: extras ?? null, odometerKm: input.odometerKm, intakeDoneAt: now(), intakeDoneBy: this.user?.id ?? null });
    this.stamps.set(id, (this.stamps.get(id) ?? 0) + 1);
  }
  async searchWorkOrders(workshopId: string, query: string, filter: WorkOrderFilter, limit = 100, signed: SignedFilter = null) {
    const q = query.trim().toLowerCase();
    const plate = plateSearchKey(query);
    const me = this.user?.id ?? "";
    return this.sorted([...this.orders.values()].filter((o) => o.workshopId === workshopId && (filter === "all" || o.status !== "closed")))
      .filter((o) => filter !== "mine" || isMyWorkOrder(o, me))
      .filter((o) => signed === null || (signed === "yes") === !!o.intakeSigned)
      .filter((o) => !q || contains(o.number, q) || (plate && plateSearchKey(o.plateNumber ?? "").includes(plate)) || contains(o.customerName, q)
        || (q.length >= 3 && [...this.items.values()].some((i) => i.workOrderId === o.id && contains(i.partNumber ?? null, q))))
      .slice(0, limit);
  }
  async workOrdersOfVehicle(vehicleId: string) {
    return this.sorted([...this.orders.values()].filter((o) => o.vehicleId === vehicleId));
  }
  async workOrdersOfCustomer(customerId: string) {
    return this.sorted([...this.orders.values()].filter((o) => o.customerId === customerId));
  }
  async getWorkOrder(id: string): Promise<WorkOrderDetail | null> {
    const o = this.orders.get(id);
    if (!o || !this.myRole(o.workshopId)) return null;
    const items = [...this.items.values()].filter((i) => i.workOrderId === id).sort((a, b) => a.sortOrder - b.sortOrder);
    const quotes = this.quotes.filter((q) => q.workOrderId === id).sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map(({ workOrderId, ...q }) => ({ ...q, itemIds: [...q.itemIds], approvedIds: [...q.approvedIds] }));
    // the quote links carry the prices: only with the right (20261220000000_roles.sql)
    const shown = this.can(o.workshopId, "quote") ? quotes : [];
    return { order: this.decorate(o), items: items.map((i) => this.priced(o.workshopId, i)), events: [...(this.events.get(id) ?? [])], quotes: shown };
  }
  /** Like public.mechanic_member_check(): only a member of the workshop who does workshop work. */
  private assertMechanic(workshopId: string, mechanicId: string | null | undefined) {
    if (!mechanicId) return;
    const m = (this.members.get(workshopId) ?? []).find((x) => x.userId === mechanicId);
    if (!m || !MECHANIC_ROLES.includes(m.role)) throw new DomainError("MECHANIC_NOT_MEMBER");
  }
  async createWorkOrder(workshopId: string, input: WorkOrderInput) {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    this.assertWritable(workshopId);
    this.assertMechanic(workshopId, input.mechanicId);
    // handed to the mechanic: the odometer comes with his intake, but only with a mechanic to do it
    const handed = !!input.intakeByMechanic;
    if (handed && !input.mechanicId) throw new DomainError("REQUIRED_MISSING");
    this.assertIntake({ ...input, intakeByMechanic: handed, intakeDoneAt: null });
    input = this.withIntakeExtras(workshopId, input);
    const year = new Date().getFullYear();
    const key = `${workshopId}-${year}`;
    const no = (this.counters.get(key) ?? 0) + 1;
    this.counters.set(key, no);
    const prefix = String(this.workshops.find((w) => w.id === workshopId)?.general.workOrderPrefix || "ML");
    const o: WorkOrder = {
      ...input,
      id: newId(),
      workshopId,
      number: `${prefix}-${year}-${String(no).padStart(5, "0")}`,
      status: "open",
      receivedAt: now(),
      readyAt: null,
      closedAt: null,
      needsReview: null,
      intakeByMechanic: handed,
      intakeDoneAt: null,
      intakeDoneBy: null,
    };
    this.orders.set(o.id, o);
    this.log(o, "created");
    return this.decorate(o);
  }
  async updateWorkOrder(id: string, input: WorkOrderInput, expectedUpdatedAt?: string) {
    const o = this.requireOpen(id);
    this.assertWritable(o.workshopId);
    const stamp = this.stamps.get(id) ?? 0;
    if (expectedUpdatedAt !== undefined && expectedUpdatedAt !== String(stamp)) throw new DomainError("WORK_ORDER_CHANGED");
    if (input.mechanicId !== undefined && input.mechanicId !== o.mechanicId) this.assertMechanic(o.workshopId, input.mechanicId);
    input = this.withIntakeExtras(o.workshopId, input, o.intakeExtras);
    // the tick: only the administrator or the owner, and never after the intake was finished
    const handed = input.intakeByMechanic === undefined ? !!o.intakeByMechanic : input.intakeByMechanic;
    if (handed !== !!o.intakeByMechanic) {
      if (o.intakeDoneAt || !this.can(o.workshopId, "close")) throw new DomainError("NOT_ALLOWED");
    }
    const next = { ...o, ...input, intakeByMechanic: handed, mechanicId: input.mechanicId !== undefined ? (input.mechanicId || null) : (o.mechanicId ?? null) };
    this.assertIntake(next);
    // only changed when the form has the picker (as the cloud store)
    this.orders.set(id, next);
    this.stamps.set(id, stamp + 1);
    return String(stamp + 1);
  }
  async setWorkOrderStatus(id: string, status: WorkOrderStatus, payment?: PaymentMethod) {
    const o = this.requireOpen(id);
    if (o.status === status) return;
    if (status !== "open" && intakePending(o)) throw new DomainError("INTAKE_PENDING");
    // closing is not a mechanic's (work_orders_role_guard)
    if (status === "closed" || (payment && payment !== o.paymentMethod)) this.requireCan(o.workshopId, "close");
    const closing = status === "closed";
    const method = closing ? (payment ?? o.paymentMethod ?? null) : (o.paymentMethod ?? null);
    const next = {
      ...o, status, readyAt: status === "ready" ? now() : o.readyAt, closedAt: closing ? now() : null,
      paymentMethod: method, paidAt: closing && (method === "cash" || method === "card") ? now() : null,
    };
    this.orders.set(id, next);
    this.log(next, status);
  }
  async markWorkOrderPaid(id: string, method: PaymentMethod | null, paid: boolean) {
    const o = this.orders.get(id);
    if (!o || o.status !== "closed") throw new DomainError("WORK_ORDER_NOT_CLOSED");
    if (!this.can(o.workshopId, "close")) throw new DomainError("NOT_ALLOWED");
    if (method && method !== o.paymentMethod && this.invoices.has(id)) throw new DomainError("INVOICE_ISSUED");
    this.orders.set(id, { ...o, paymentMethod: method ?? o.paymentMethod ?? null, paidAt: paid ? (o.paidAt ?? now()) : null });
  }
  private invoices = new Map<string, Invoice>();
  private invoiceKeys = new Set<string>();
  /** Test helper: what the provider "answers" for the next invoice (null: an error, "?": no answer). */
  invoiceAnswer: string | null = "E-TESZT-2026-1";
  async invoiceOf(workOrderId: string) {
    const o = this.orders.get(workOrderId);
    if (!o || !this.can(o.workshopId, "invoice")) return null;
    return this.invoices.get(workOrderId) ?? null;
  }
  async issueInvoice(workOrderId: string) {
    const o = this.orders.get(workOrderId);
    if (!o || !this.can(o.workshopId, "invoice")) throw new DomainError("NOT_ALLOWED");
    if (o.status !== "closed") throw new DomainError("WORK_ORDER_NOT_CLOSED");
    const c = o.customerId ? this.customers.get(o.customerId) : undefined;
    if (!c?.postalCode?.trim() || !c.city?.trim() || !c.addressLine?.trim()) throw new DomainError("INVOICE_CUSTOMER_DATA");
    if (!this.invoiceKeys.has(o.workshopId)) throw new DomainError("INVOICE_KEY_MISSING");
    const existing = this.invoices.get(workOrderId);
    if (existing?.status === "issued") return existing;
    if (existing) throw new DomainError("INVOICE_UNKNOWN", existing.status);
    if (!this.invoiceAnswer) throw new DomainError("INVOICE_FAILED", "3: Hibás kulcs");
    if (this.invoiceAnswer === "?") {
      this.invoices.set(workOrderId, { status: "unknown", number: null, gross: null, currency: "HUF", viewUrl: null, issuedAt: null });
      throw new DomainError("INVOICE_UNKNOWN", "network");
    }
    const gross = [...this.items.values()].filter((i) => i.workOrderId === workOrderId && i.approval !== "declined").reduce((s, i) => s + i.gross, 0);
    const inv: Invoice = { status: "issued", number: this.invoiceAnswer, gross, currency: "HUF", viewUrl: "https://www.szamlazz.hu/szamla/?page=teszt", issuedAt: now() };
    this.invoices.set(workOrderId, inv);
    return inv;
  }
  async invoiceKeySet(workshopId: string) {
    return this.invoiceKeys.has(workshopId);
  }
  async setInvoiceKey(workshopId: string, key: string) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_OWNER");
    if (key.trim()) this.invoiceKeys.add(workshopId);
    else this.invoiceKeys.delete(workshopId);
  }
  async resolveInvoice(workOrderId: string, number: string | null) {
    const o = this.orders.get(workOrderId);
    const inv = this.invoices.get(workOrderId);
    const role = o ? this.myRole(o.workshopId) : null;
    if (!o || !inv || inv.status === "issued" || (role !== "owner" && role !== "manager")) throw new DomainError("NOT_ALLOWED");
    if (number?.trim()) this.invoices.set(workOrderId, { ...inv, status: "issued", number: number.trim(), issuedAt: now() });
    else this.invoices.delete(workOrderId);
  }
  private cancelled = new Map<string, CancelledInvoice[]>();
  /** Test helper: what the provider "answers" for the next cancellation (null: an error, "?": no answer). */
  cancelAnswer: string | null = "E-TESZT-2026-S1";
  async cancelInvoice(workOrderId: string, reason: string) {
    const o = this.orders.get(workOrderId);
    const inv = this.invoices.get(workOrderId);
    const role = o ? this.myRole(o.workshopId) : null;
    if (!o || !inv || inv.status !== "issued" || (role !== "owner" && role !== "manager")) throw new DomainError("NOT_ALLOWED");
    if (!reason.trim()) throw new DomainError("INVOICE_CANCEL_REASON");
    if (!this.invoiceKeys.has(o.workshopId)) throw new DomainError("INVOICE_KEY_MISSING");
    if (inv.cancelStatus) throw new DomainError("INVOICE_CANCEL_UNKNOWN", inv.cancelStatus);
    if (!this.cancelAnswer) throw new DomainError("INVOICE_CANCEL_FAILED", "57: A számla nem sztornózható");
    if (this.cancelAnswer === "?") {
      this.invoices.set(workOrderId, { ...inv, cancelStatus: "unknown", cancelReason: reason.trim() });
      throw new DomainError("INVOICE_CANCEL_UNKNOWN", "network");
    }
    this.finishCancel(workOrderId, inv, this.cancelAnswer, reason.trim());
    return { number: this.cancelAnswer };
  }
  private finishCancel(workOrderId: string, inv: Invoice, number: string, reason: string) {
    const list = this.cancelled.get(workOrderId) ?? [];
    list.unshift({ number: inv.number, gross: inv.gross, currency: inv.currency, issuedAt: inv.issuedAt, cancelNumber: number, cancelledAt: now(), cancelReason: reason });
    this.cancelled.set(workOrderId, list);
    this.invoices.delete(workOrderId);
  }
  async resolveInvoiceCancel(workOrderId: string, number: string | null) {
    const o = this.orders.get(workOrderId);
    const inv = this.invoices.get(workOrderId);
    const role = o ? this.myRole(o.workshopId) : null;
    if (!o || !inv || inv.cancelStatus !== "unknown" || (role !== "owner" && role !== "manager")) throw new DomainError("NOT_ALLOWED");
    if (number?.trim()) this.finishCancel(workOrderId, inv, number.trim(), inv.cancelReason ?? "");
    else this.invoices.set(workOrderId, { ...inv, cancelStatus: null, cancelReason: null });
  }
  async cancelledInvoices(workOrderId: string) {
    const o = this.orders.get(workOrderId);
    if (!o || !this.myRole(o.workshopId)) return [];
    return [...(this.cancelled.get(workOrderId) ?? [])];
  }
  async reopenWorkOrder(id: string, reason: string) {
    const o = this.orders.get(id);
    if (!o || o.status !== "closed") throw new DomainError("WORK_ORDER_NOT_CLOSED");
    if (!this.hasPermission(o.workshopId, "workOrder.reopen")) throw new DomainError("NOT_ALLOWED");
    if (this.invoices.has(id)) throw new DomainError("INVOICE_ISSUED");
    if (!reason.trim()) throw new DomainError("REOPEN_REASON_REQUIRED");
    this.orders.set(id, { ...o, status: "open", closedAt: null, paidAt: null });
    this.log(o, "reopened", reason.trim());
  }
  async deleteWorkOrder(id: string) {
    const o = this.requireOpen(id);
    if (!this.hasPermission(o.workshopId, "workOrder.delete")) throw new DomainError("NOT_ALLOWED");
    this.orders.delete(id);
    for (const i of [...this.items.values()]) if (i.workOrderId === id) {
      this.items.delete(i.id);
      this.syncItemStock(i.id, null);
    }
  }
  async saveWorkOrderItem(workshopId: string, workOrderId: string, line: WorkOrderLineInput, id?: string) {
    this.assertWritable(workshopId);
    this.requireOpen(workOrderId);
    this.requireCan(workshopId, "items");
    let built = buildLine(line);
    const existing = id ? this.items.get(id) : undefined;
    if (!existing && intakePending(this.orders.get(workOrderId)!)) throw new DomainError("INTAKE_PENDING");
    // like work_order_items_price_guard: who does not see prices does not set them
    if (!this.can(workshopId, "prices")) {
      const a = built.articleId && (!existing || built.articleId !== existing.articleId) ? this.articles.get(built.articleId) : undefined;
      const price = a?.salePrice != null ? { unitPrice: a.salePrice, priceMode: a.salePriceMode ?? "gross", vatRate: a.vatRate ?? 27 }
        : existing ? { unitPrice: existing.unitPrice, priceMode: existing.priceMode, vatRate: existing.vatRate } : { unitPrice: 0, priceMode: built.priceMode, vatRate: built.vatRate };
      built = buildLine({ ...line, ...price, approval: existing ? existing.approval : line.approval === "pending" ? "pending" : "none" });
    }
    if (built.mechanicId && built.mechanicId !== existing?.mechanicId) this.assertMechanic(workshopId, built.mechanicId);
    const sortOrder = existing?.sortOrder ?? [...this.items.values()].filter((i) => i.workOrderId === workOrderId).length + 1;
    const item: WorkOrderItem = { ...built, id: existing?.id ?? newId(), workOrderId, sortOrder };
    // a changed price, quantity or text needs a new yes (as the database does)
    if (existing && (existing.approval === "approved" || existing.approval === "declined") && item.approval === existing.approval &&
        (item.gross !== existing.gross || item.quantity !== existing.quantity || item.description !== existing.description)) item.approval = "pending";
    this.items.set(item.id, item);
    this.syncItemStock(item.id, item);
    if (!existing && item.articleId) {
      const a = this.articles.get(item.articleId);
      if (a) this.articles.set(a.id, { ...a, usageCount: a.usageCount + 1, lastUsed: new Date().toISOString().slice(0, 10) });
    }
    return this.priced(workshopId, item);
  }
  // ---- quotes ----
  private quotes: (QuoteRequest & { workOrderId: string })[] = [];
  private pendingOf(workOrderId: string) {
    return [...this.items.values()].filter((i) => i.workOrderId === workOrderId && i.approval === "pending").sort((a, b) => a.sortOrder - b.sortOrder).map((i) => i.id);
  }
  private applyAnswer(q: QuoteRequest & { workOrderId: string }, approvedIds: string[]) {
    for (const id of q.itemIds) {
      const i = this.items.get(id);
      if (!i || i.approval !== "pending") continue;
      const next = { ...i, approval: approvedIds.includes(id) ? ("approved" as const) : ("declined" as const) };
      this.items.set(id, next);
      this.syncItemStock(id, next);
    }
    q.answeredAt = now();
    q.approvedIds = q.itemIds.filter((x) => approvedIds.includes(x));
    const o = this.orders.get(q.workOrderId);
    if (o) this.log(o, "quote_answered", q.channel);
  }
  private startQuote(workOrderId: string, channel: QuoteRequest["channel"], note: string | null) {
    const o = this.requireOpen(workOrderId);
    const w = this.workshops.find((x) => x.id === o.workshopId);
    if (!w || !this.can(w.id, "quote")) throw new DomainError("NOT_ALLOWED");
    // a link needs the feature; an answer by phone is always possible (like record_quote_answer), so lines
    // waiting for approval never block closing the work order
    if (channel === "link" && !featureOn(w, "quotes")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    const itemIds = this.pendingOf(workOrderId);
    if (itemIds.length === 0) throw new DomainError("QUOTE_NOTHING_PENDING");
    for (const q of this.quotes) if (q.workOrderId === workOrderId && !q.answeredAt && q.expiresAt > now()) q.expiresAt = now();
    const q = { id: newId(), token: Array.from(globalThis.crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2, "0")).join(""), channel, itemIds, createdAt: now(), expiresAt: new Date(Date.now() + 14 * 864e5).toISOString(),
      answeredAt: null as string | null, approvedIds: [] as string[], note, workOrderId };
    this.quotes.push(q);
    return { o, q };
  }
  /** Messages "sent" in demo mode (nothing leaves the browser); tests read them. */
  sentMessages: Array<{ workshopId: string; workOrderId: string | null; channel: QuoteChannel | "smsTest"; to: string; price: number; at?: string; kind?: "ready" } & QuoteMessage> = [];
  private smsPhones = new Set<string>();
  private smsTokens = new Map<string, { workshopId: string; expires: number; used: boolean }>();
  private smsPrice(workshopId: string) {
    const w = this.workshops.find((x) => x.id === workshopId);
    return w?.general.smsProvider === "ownPhone" ? 0 : SMS_PRICE_HUF;
  }
  private smsReady(workshopId: string) {
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w?.features.includes("sms")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (w.general.smsProvider === "ownPhone" && !this.smsPhones.has(workshopId)) throw new DomainError("SEND_FAILED");
  }
  // ---- reminders ----
  /** Sent reminders (demo: nothing leaves the browser); tests read them. */
  reminderLog: (SentReminder & { workshopId: string; text: string })[] = [];
  /** The day the demo and the tests count as today (null: the real today). */
  today: string | null = null;
  async saveVehicleDates(vehicleId: string, dates: Partial<ReminderDatesInput>) {
    const v = this.vehicles.get(vehicleId);
    if (!v || !this.myRole(v.workshopId)) throw new DomainError("NOT_ALLOWED");
    this.vehicles.set(vehicleId, { ...v, ...dates });
  }
  /** Same rules as public.reminder_rows() in the database. */
  private reminderRows(workshopId: string, today: string): UpcomingReminder[] {
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w) return [];
    const rs = reminderSettings(w.general);
    const out: UpcomingReminder[] = [];
    for (const v of this.vehicles.values()) {
      if (v.workshopId !== workshopId) continue;
      const c = v.customerId ? this.customers.get(v.customerId) : undefined;
      const consent = !!c?.reminderConsentAt && (c.reminderChannels ?? []).length > 0;
      const email = !!c?.email?.trim();
      const phone = !!(c?.mobile?.trim() || c?.phone?.trim());
      const dues: [ReminderKind, string | null | undefined, number | null][] = [
        ["inspection", v.inspectionValidUntil, null],
        ["service", v.nextServiceDate, v.nextServiceKm ?? null],
      ];
      for (const [kind, due, km] of dues) {
        if (!due || due < today) continue;
        const sentChannels = cleanChannels(this.reminderLog.filter((l) => l.vehicleId === v.id && l.kind === kind && l.dueDate === due).map((l) => l.channel));
        const channels = consent
          ? cleanChannels(c!.reminderChannels).filter((ch: ReminderChannel) =>
              !sentChannels.includes(ch) && (ch === "email" ? rs.email && email : rs.sms && w.features.includes("sms") && phone))
          : [];
        out.push({
          vehicleId: v.id, plateNumber: v.plateNumber, plateCountry: v.plateCountry ?? null, vehicleLabel: [v.make, v.model].filter(Boolean).join(" ") || null,
          customerId: c?.id ?? null, customerName: c?.name ?? null, hasEmail: email, hasPhone: phone, consent,
          customerEmail: c?.email?.trim() || null, customerPhone: c?.phone?.trim() || null, customerMobile: c?.mobile?.trim() || null,
          kind, dueDate: due, dueKm: km, sendFrom: reminderSendDate(due, rs.days[kind]), channels, sentChannels,
          wanted: (kind === "inspection" ? v.remindInspection : v.remindService) !== false,
        });
      }
    }
    return out.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.plateNumber.localeCompare(b.plateNumber) || a.kind.localeCompare(b.kind));
  }
  async upcomingReminders(workshopId: string, days = 60) {
    if (!this.myRole(workshopId)) return [];
    const today = this.today ?? todayIso();
    return this.reminderRows(workshopId, today).filter((r) => r.dueDate <= addDays(today, days));
  }
  async sentReminders(workshopId: string) {
    if (!this.myRole(workshopId)) return [];
    return this.reminderLog.filter((l) => l.workshopId === workshopId).sort((a, b) => b.sentAt.localeCompare(a.sentAt)).map(({ workshopId: _w, text: _t, ...l }) => l);
  }
  private sendReminderRow(r: UpcomingReminder, workshopId: string, manual: boolean) {
    const c = this.customers.get(r.customerId!)!;
    const sent: { channel: ReminderChannel; to: string }[] = [];
    let price = 0;
    for (const channel of r.channels) {
      const to = channel === "email" ? c.email!.trim() : (c.mobile || c.phone)!.trim();
      const p = channel === "sms" ? this.smsPrice(workshopId) : 0;
      this.reminderLog.push({ id: newId(), workshopId, vehicleId: r.vehicleId, plateNumber: r.plateNumber, plateCountry: r.plateCountry, kind: r.kind, dueDate: r.dueDate,
        channel, recipient: channel === "email" ? to : `…${to.slice(-4)}`, manual, sentAt: now(), text: `${r.kind} ${r.plateNumber} ${r.dueDate}` });
      if (channel === "sms") this.sentMessages.push({ workshopId, workOrderId: null, channel: "sms", to, price: p, at: now(), subject: "", text: `${r.kind} ${r.plateNumber}`, link: "", buttonLabel: "" });
      sent.push({ channel, to });
      price += p;
    }
    return { sent, price };
  }
  async sendReminderNow(vehicleId: string, kind: ReminderKind) {
    const v = this.vehicles.get(vehicleId);
    if (!v || !this.can(v.workshopId, "reminders")) throw new DomainError("NOT_ALLOWED");
    const w = this.workshops.find((x) => x.id === v.workshopId)!;
    const r = this.reminderRows(v.workshopId, this.today ?? todayIso()).find((x) => x.vehicleId === vehicleId && x.kind === kind);
    if (!featureOn(w, "reminders") || !r || !r.wanted || !r.consent || r.channels.length === 0) throw new DomainError("REMINDER_NOTHING_TO_SEND");
    return this.sendReminderRow(r, v.workshopId, true);
  }
  /** The daily job of the demo and the tests (the cloud runs public.due_reminders() on the server). */
  runDailyReminders(today = this.today ?? todayIso()) {
    let count = 0;
    for (const w of this.workshops) {
      if (!featureOn(w, "reminders")) continue;
      for (const r of this.reminderRows(w.id, today)) {
        if (!r.wanted || !r.consent || r.channels.length === 0 || today < r.sendFrom) continue;
        count += this.sendReminderRow(r, w.id, false).sent.length;
      }
    }
    return count;
  }
  async sendTestSms(workshopId: string, to: string, text: string) {
    const role = this.myRole(workshopId);
    if (role !== "owner" && role !== "manager") throw new DomainError("NOT_ALLOWED");
    this.smsReady(workshopId);
    const price = this.smsPrice(workshopId);
    this.sentMessages.push({ workshopId, workOrderId: null, channel: "smsTest", to, price, at: now(), subject: "", text, link: "", buttonLabel: "" });
    return { to, price };
  }
  async smsUsage(workshopId: string) {
    const mine = this.sentMessages.filter((m) => (m.channel === "sms" || m.channel === "smsTest") && m.workshopId === workshopId);
    return { count: mine.length, paidCount: mine.filter((m) => m.price > 0).length, amount: mine.reduce((a, m) => a + m.price, 0), currency: "HUF" };
  }
  async smsPhoneSet(workshopId: string) {
    return this.smsPhones.has(workshopId);
  }
  async createSmsSetupToken(workshopId: string) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_OWNER");
    const token = [...crypto.getRandomValues(new Uint8Array(32))].map((b) => b.toString(16).padStart(2, "0")).join("");
    this.smsTokens.set(token, { workshopId, expires: Date.now() + 30 * 60_000, used: false });
    return token;
  }
  async smsSetupInfo(token: string) {
    const t = this.smsTokens.get(token);
    if (!t || t.used || t.expires < Date.now()) return null;
    return this.workshops.find((w) => w.id === t.workshopId)?.name ?? null;
  }
  async completeSmsSetup(token: string, user: string, password: string) {
    const t = this.smsTokens.get(token);
    if (!t || t.used || t.expires < Date.now()) throw new DomainError("NOT_ALLOWED");
    if (!user.trim() || !password.trim()) throw new DomainError("REQUIRED_MISSING");
    t.used = true;
    this.smsPhones.add(t.workshopId);
  }
  private billing = new Map<string, OpBilling>();
  // ---- subscription (the workshop's plan field holds the effective plan; subs the stored one) ----
  private pricing: Pricing = JSON.parse(JSON.stringify(DEFAULT_PRICING)) as Pricing;
  private subs = new Map<string, SubscriptionState>();
  /** Tests: moves the trial end of a workshop (the database tests fake the date the same way). */
  setTrialUntil(workshopId: string, day: string | null) {
    const sub = this.subs.get(workshopId);
    if (sub) sub.trialUntil = day;
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) this.refreshTrial(w);
  }
  /** Tests: today is day `day` of the trial (1: its first day, 31: the day after it); the Profi week is its first 7 days. */
  setTrialDay(workshopId: string, day: number) {
    const sub = this.subs.get(workshopId);
    if (!sub) return;
    const start = addDays(todayIso(), 1 - day);
    const p = { ...this.pricing, trialDays: this.trialDays || this.pricing.trialDays };
    Object.assign(sub, { trialStarted: start, trialUntil: addDays(start, p.trialDays - 1), trialUpgradeUntil: addDays(start, p.trialUpgradeDays - 1), trialUpgradePlan: p.trialUpgradePlan });
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) this.refreshTrial(w);
  }
  /** The trial runs, or ended without a choice (read only): features the plan lacks are kept, not switched off. */
  private trialUnsettled(workshopId: string): boolean {
    const sub = this.subs.get(workshopId);
    if (!sub?.trialUntil) return false;
    const s = { ...sub, status: this.billingOf(workshopId).status };
    return inTrial(s, todayIso()) || readOnly(s, todayIso());
  }
  /**
   * The plan field follows the trial: Profi in its first week, the stored plan after it. Features the plan lacks
   * stay switched on while the trial is not settled (they come back with Profi); after it they are switched off.
   */
  private refreshTrial(w: Workshop) {
    const sub = this.subs.get(w.id);
    // only a workshop with a trial follows it (otherwise the plan field is the operator's plan as it is)
    if (!sub?.trialUntil) return;
    const plan = effectivePlan({ ...sub, status: this.billingOf(w.id).status }, todayIso());
    (w as { plan: Plan }).plan = plan;
    if (!this.trialUnsettled(w.id)) w.features = w.features.filter((f) => featureAvailable(plan, f, w.extraFeatures));
  }
  /** What the workshop used since its trial started that Alap lacks (like public.trial_usage). */
  private trialUsage(workshopId: string): TrialUsage | null {
    const sub = this.subs.get(workshopId);
    if (!sub?.trialStarted || !inTrial(sub, todayIso())) return null;
    const since = sub.trialStarted;
    const day = (iso: string | null | undefined) => (iso ? todayIso(new Date(iso)) : "");
    const orders = new Set([...this.orders.values()].filter((o) => o.workshopId === workshopId).map((o) => o.id));
    return {
      quoteLinks: this.quotes.filter((q) => orders.has(q.workOrderId) && q.channel === "link" && day(q.createdAt) >= since).length,
      stockMovements: this.movements.filter((m) => m.workshopId === workshopId && day(m.createdAt) >= since).length,
      reminders: [...this.vehicles.values()].filter((v) => {
        if (v.workshopId !== workshopId || !(v.inspectionValidUntil || v.nextServiceDate || v.nextServiceKm != null)) return false;
        const c = v.customerId ? this.customers.get(v.customerId) : undefined;
        return (c?.reminderChannels ?? []).length > 0;
      }).length,
    };
  }
  /** Read only after the trial without a choice (like the database's read_only_guard on the business tables). */
  private assertWritable(workshopId: string) {
    const sub = this.subs.get(workshopId);
    if (sub && readOnly({ ...sub, status: this.billingOf(workshopId).status }, todayIso())) throw new DomainError("READ_ONLY");
  }
  private subOf(workshopId: string): SubscriptionState {
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!this.subs.has(workshopId)) this.subs.set(workshopId, { ...EMPTY_SUBSCRIPTION, plan: w?.plan ?? "basic" });
    return this.subs.get(workshopId)!;
  }
  async mySubscription(workshopId: string): Promise<MySubscription> {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) this.refreshTrial(w);
    const sub = this.subOf(workshopId);
    const b = this.billingOf(workshopId);
    const x = this.billingExtra(workshopId);
    const month = new Date().toISOString().slice(0, 7);
    return {
      ...sub, lockedPrices: sub.lockedPrices ? JSON.parse(JSON.stringify(sub.lockedPrices)) : null,
      status: b.status, monthlyFee: b.monthlyFee, discountPct: x.discountPct,
      today: todayIso(), effectivePlan: w?.plan ?? sub.plan, readOnly: readOnly({ ...sub, status: b.status }, todayIso()), trialUsage: this.trialUsage(workshopId), pricing: JSON.parse(JSON.stringify(this.pricing)) as Pricing,
      seats: { ...MemoryStore.SEATS }, aiCredit: { ...this.aiSettings.allowance },
      seatsUsed: (this.members.get(workshopId) ?? []).length, seatLimit: this.seatLimit(workshopId),
      smsNet: this.sentMessages.filter((m) => m.workshopId === workshopId && (m.channel === "sms" || m.channel === "smsTest") && (m.at ?? new Date().toISOString()).startsWith(month)).reduce((a, m) => a + m.price, 0),
      aiCreditGross: this.aiBought.filter((a) => a.workshopId === workshopId && a.at.startsWith(month)).reduce((a, m) => a + m.amount, 0),
    };
  }
  async setSubscription(workshopId: string, patch: SubscriptionPatch) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_OWNER");
    this.mfaAssert();
    const sub = this.subOf(workshopId);
    const bad = Object.keys(patch).some((k) => !["plan", "period", "extraSeats"].includes(k))
      || (patch.plan !== undefined && (!["basic", "medium", "premium"].includes(patch.plan) || !sub.trialUntil
          || (sub.trialUntil < todayIso() && !readOnly({ ...sub, status: this.billingOf(workshopId).status }, todayIso()))))
      || (patch.period !== undefined && !["monthly", "yearly"].includes(patch.period))
      || (patch.extraSeats !== undefined && (!Number.isInteger(patch.extraSeats) || patch.extraSeats < 0 || patch.extraSeats > 1000));
    if (bad) throw new DomainError("NOT_ALLOWED");
    if (patch.plan) {
      // paid from today: a choice after the trial (ends read only), or Profi / Premium during it (ends the trial);
      // Alap during the trial is only recorded
      const ro = readOnly({ ...sub, status: this.billingOf(workshopId).status }, todayIso());
      if (ro || choiceEndsTrial(patch.plan)) {
        if (!ro) sub.trialUntil = addDays(todayIso(), -1);
        if (this.billingOf(workshopId).status === "trial") this.billing.set(workshopId, { ...this.billingOf(workshopId), status: "active" });
        sub.billingAnchor = todayIso();
        if (sub.founder) Object.assign(sub, { founderUntil: addDays(addMonths(todayIso(), this.pricing.founder.months), -1), priceLockUntil: addDays(addMonths(todayIso(), this.pricing.founder.lockMonths), -1) });
      }
      sub.plan = patch.plan;
      sub.planAfterTrial = patch.plan;
      const w = this.workshops.find((x) => x.id === workshopId);
      if (w) this.refreshTrial(w);
    }
    if (patch.period) {
      sub.period = patch.period;
      if (patch.period === "yearly" && !sub.billingAnchor) sub.billingAnchor = addMonths(`${todayIso().slice(0, 7)}-01`, 1);
    }
    if (patch.extraSeats !== undefined) sub.extraSeats = patch.extraSeats;
    this.opLogs.unshift({ workshopId, at: new Date().toISOString(), adminEmail: this.user?.email ?? null, action: "ownerSubscription", details: patch });
  }
  async opSubscriptions(): Promise<OpSubscription[]> {
    await this.opAssert();
    return this.workshops.map((w) => {
      this.refreshTrial(w);
      const sub = this.subOf(w.id);
      return {
        workshopId: w.id, effectivePlan: w.plan, readOnly: readOnly({ ...sub, status: this.billingOf(w.id).status }, todayIso()), trialUntil: sub.trialUntil, planAfterTrial: sub.planAfterTrial, period: sub.period,
        extraSeats: sub.extraSeats, billingAnchor: sub.billingAnchor, founder: sub.founder, founderUntil: sub.founderUntil,
        priceLockUntil: sub.priceLockUntil, lockedPrices: sub.lockedPrices,
      };
    });
  }
  async opUpdateSubscription(workshopId: string, p: OpSubscriptionPatch) {
    await this.opAssert();
    const bad = !this.workshops.some((w) => w.id === workshopId)
      || Object.keys(p).some((k) => !["trialUntil", "period", "extraSeats", "founder", "founderUntil"].includes(k))
      || (p.period !== undefined && !["monthly", "yearly"].includes(p.period))
      || (p.extraSeats !== undefined && (!Number.isInteger(p.extraSeats) || p.extraSeats < 0 || p.extraSeats > 1000));
    if (bad) throw new DomainError("NOT_ALLOWED");
    await this.opAssert(workshopId, "subscription", p);
    const sub = this.subOf(workshopId);
    if (p.trialUntil !== undefined) {
      sub.trialUntil = p.trialUntil;
      if (p.trialUntil) sub.billingAnchor = addDays(p.trialUntil, 1);
    }
    if (p.period) sub.period = p.period;
    if (p.extraSeats !== undefined) sub.extraSeats = p.extraSeats;
    if (p.founder !== undefined) {
      const anchor = sub.billingAnchor ?? addMonths(`${todayIso().slice(0, 7)}-01`, 1);
      const terms = newWorkshopTerms({ ...this.pricing, trialDays: 0 }, anchor, p.founder);
      Object.assign(sub, { founder: p.founder, billingAnchor: p.founder ? anchor : sub.billingAnchor, founderUntil: terms.founderUntil, priceLockUntil: terms.priceLockUntil, lockedPrices: terms.lockedPrices });
    }
    if (p.founderUntil !== undefined) sub.founderUntil = p.founderUntil;
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) this.refreshTrial(w);
  }
  async opPricing(): Promise<Pricing> {
    await this.opAssert();
    return JSON.parse(JSON.stringify(this.pricing)) as Pricing;
  }
  async opSetPricing(pricing: Pricing) {
    await this.opAssert();
    const p = parsePricing(pricing);
    if (!p) throw new DomainError("NOT_ALLOWED");
    this.pricing = p;
    this.opLogs.unshift({ workshopId: "", at: new Date().toISOString(), adminEmail: this.user!.email, action: "pricing", details: p });
  }
  private opLogs: (OpLogEntry & { workshopId: string })[] = [];
  async myBillingStatus(workshopId: string) {
    return this.billing.get(workshopId)?.status ?? "trial";
  }
  async isPlatformAdmin() {
    return !!this.user && this.operators.includes(this.user.email);
  }
  private async opAssert(workshopId?: string, action?: string, details?: unknown) {
    if (!(await this.isPlatformAdmin())) throw new DomainError("NOT_ALLOWED");
    // like public.op_assert: the real code (aal2), a remembered device does not count here
    if (this.aal !== "aal2" && (this.mfaFactors.has(this.user!.id) || this.mfaRequired)) {
      throw new DomainError(this.mfaFactors.has(this.user!.id) ? "MFA_REQUIRED" : "MFA_SETUP_REQUIRED");
    }
    if (workshopId && action) this.opLogs.unshift({ workshopId, at: new Date().toISOString(), adminEmail: this.user!.email, action, details });
  }
  async opWorkshops(): Promise<OpWorkshop[]> {
    await this.opAssert();
    const month = new Date().toISOString().slice(0, 7);
    return this.workshops
      .map((w) => {
        const members = this.members.get(w.id) ?? [];
        const orders = [...this.orders.values()].filter((o) => o.workshopId === w.id);
        const sms = this.sentMessages.filter((m) => m.workshopId === w.id && (m.channel === "sms" || m.channel === "smsTest"));
        const b = this.billing.get(w.id) ?? { status: "trial" as const, monthlyFee: null, paidUntil: null, note: null };
        void month;
        return {
          id: w.id, name: w.name, plan: w.plan, features: [...w.features], createdAt: new Date().toISOString(),
          ownerEmail: members.find((m) => m.role === "owner")?.email ?? null, memberCount: members.length,
          workOrderCount: orders.length, lastActivity: null, termsAccepted: this.terms.some((a) => a.workshopId === w.id),
          smsCount: sms.length, smsAmount: sms.reduce((a, m) => a + m.price, 0), ...b,
          ...this.billingExtra(w.id), featureGrants: [...(w.extraFeatures ?? [])].sort(),
          giftMonthHuf: this.aiGifts.filter((g) => g.workshopId === w.id && g.at.startsWith(month)).reduce((a, g) => a + g.amount, 0),
          giftTotalHuf: this.aiGifts.filter((g) => g.workshopId === w.id).reduce((a, g) => a + g.amount, 0),
          lastSignIn: members.map((m) => this.signIns.get(m.userId) ?? "").sort().at(-1) || null,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  async opMembers(workshopId: string) {
    await this.opAssert();
    return (this.members.get(workshopId) ?? []).map((m) => ({ email: m.email, role: m.role }));
  }
  async opSetPlan(workshopId: string, plan: Plan) {
    const w = this.workshops.find((x) => x.id === workshopId);
    await this.opAssert(workshopId, "plan", { from: w?.plan, to: plan });
    if (!w) return;
    const sub = this.subs.get(w.id);
    if (sub) sub.plan = plan;
    if (sub && sub.trialUntil) sub.planAfterTrial = plan;
    if (sub && sub.trialUntil && sub.trialUntil < todayIso() && this.billingOf(w.id).status === "trial") this.billing.set(w.id, { ...this.billingOf(w.id), status: "active" });
    (w as { plan: Plan }).plan = sub ? effectivePlan({ ...sub, status: this.billingOf(w.id).status }, todayIso()) : plan;
    if (!this.trialUnsettled(w.id)) w.features = w.features.filter((f) => featureAvailable(w.plan, f, w.extraFeatures));
  }
  async opSetFeatures(workshopId: string, features: string[]) {
    await this.opAssert(workshopId, "features", features);
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w) w.features = features.filter((f) => featureAvailable(w.plan, f, w.extraFeatures));
  }
  async opSetBilling(workshopId: string, b: OpBilling) {
    await this.opAssert(workshopId, "billing", b);
    this.billing.set(workshopId, { ...b });
  }
  private extras = new Map<string, { discountPct: number | null; aiAllowance: number | null; tags: string[] }>();
  private billingExtra(workshopId: string) {
    const x = this.extras.get(workshopId);
    return { discountPct: x?.discountPct ?? null, aiAllowance: x?.aiAllowance ?? null, tags: [...(x?.tags ?? [])] };
  }
  private billingOf(workshopId: string): OpBilling {
    return { ...(this.billing.get(workshopId) ?? { status: "trial", monthlyFee: null, paidUntil: null, note: null }) };
  }
  private static STATUSES: string[] = ["trial", "active", "overdue", "suspended", "free"];
  private static cleanTags(tags: string[]) {
    const clean = tags.map((x) => x.trim());
    if (clean.some((x) => x.length < 1 || x.length > 30)) throw new DomainError("NOT_ALLOWED");
    return MemoryStore.sortTags([...new Set(clean)]);
  }
  /** The same order as the database: case-insensitive, then exact. */
  private static sortTags(tags: string[]) {
    return tags.sort((a, b) => (a.toLowerCase() < b.toLowerCase() ? -1 : a.toLowerCase() > b.toLowerCase() ? 1 : a < b ? -1 : a > b ? 1 : 0));
  }
  async opUpdateBilling(workshopId: string, p: OpBillingPatch) {
    await this.opAssert();
    const bad = !this.workshops.some((w) => w.id === workshopId) || (p.status !== undefined && !MemoryStore.STATUSES.includes(p.status))
      || (p.monthlyFee != null && p.monthlyFee < 0) || (p.discountPct != null && (p.discountPct < 0 || p.discountPct > 100)) || (p.aiAllowance != null && p.aiAllowance < 0);
    if (bad) throw new DomainError("NOT_ALLOWED");
    const tags = p.tags === undefined ? undefined : MemoryStore.cleanTags(p.tags);
    await this.opAssert(workshopId, "billing", p);
    const b = this.billingOf(workshopId);
    if (p.status !== undefined) b.status = p.status;
    if (p.monthlyFee !== undefined) b.monthlyFee = p.monthlyFee === null ? null : Math.round(p.monthlyFee);
    if (p.paidUntil !== undefined) b.paidUntil = p.paidUntil;
    if (p.note !== undefined) b.note = p.note?.trim() || null;
    this.billing.set(workshopId, b);
    const x = this.billingExtra(workshopId);
    if (p.discountPct !== undefined) x.discountPct = p.discountPct;
    if (p.aiAllowance !== undefined) x.aiAllowance = p.aiAllowance === null ? null : Math.round(p.aiAllowance);
    if (tags !== undefined) x.tags = tags;
    this.extras.set(workshopId, x);
    const l = this.aiOwnLimits.get(workshopId) ?? { dailyCapHuf: null, purchaseLimitHuf: null };
    if (p.aiDailyCapHuf !== undefined) l.dailyCapHuf = p.aiDailyCapHuf === null ? null : Math.round(p.aiDailyCapHuf);
    if (p.aiPurchaseLimitHuf !== undefined) l.purchaseLimitHuf = p.aiPurchaseLimitHuf === null ? null : Math.round(p.aiPurchaseLimitHuf);
    this.aiOwnLimits.set(workshopId, l);
    const c = this.creditOwn.get(workshopId) ?? { allowance: null, purchaseLimit: null };
    for (const v of [p.aiAllowanceCredits, p.aiPurchaseLimitCredits]) if (v != null && (!Number.isInteger(v) || v < 0)) throw new DomainError("NOT_ALLOWED");
    if (p.aiAllowanceCredits !== undefined) c.allowance = p.aiAllowanceCredits;
    if (p.aiPurchaseLimitCredits !== undefined) c.purchaseLimit = p.aiPurchaseLimitCredits;
    this.creditOwn.set(workshopId, c);
  }
  private aiOwnLimits = new Map<string, { dailyCapHuf: number | null; purchaseLimitHuf: number | null }>();
  async opMemberList(workshopId: string): Promise<OpMember[]> {
    await this.opAssert();
    return (this.members.get(workshopId) ?? []).map((m) => ({ userId: m.userId, email: m.email, role: m.role, createdAt: null, lastSignIn: this.signIns.get(m.userId) ?? null, mfa: this.mfaFactors.has(m.userId) }));
  }
  async opSetMemberRole(workshopId: string, userId: string, role: Role) {
    await this.opAssert();
    if (!isRole(role)) throw new DomainError("INVALID_ROLE", role);
    const m = (this.members.get(workshopId) ?? []).find((x) => x.userId === userId);
    if (!m) throw new DomainError("NOT_ALLOWED");
    if (m.role === "owner" && role !== "owner" && this.wouldLeaveNoOwner(workshopId, userId)) throw new DomainError("LAST_OWNER");
    await this.opAssert(workshopId, "memberRole", { email: m.email, from: m.role, to: role });
    m.role = role;
  }
  async opRemoveMember(workshopId: string, userId: string) {
    await this.opAssert();
    const list = this.members.get(workshopId) ?? [];
    const m = list.find((x) => x.userId === userId);
    if (!m) throw new DomainError("NOT_ALLOWED");
    if (m.role === "owner" && this.wouldLeaveNoOwner(workshopId, userId)) throw new DomainError("LAST_OWNER");
    await this.opAssert(workshopId, "memberRemove", { email: m.email, role: m.role });
    this.members.set(workshopId, list.filter((x) => x.userId !== userId));
  }
  async opSendPasswordReset(workshopId: string, email: string) {
    await this.opAssert();
    const e = email.trim().toLowerCase();
    if (!(this.members.get(workshopId) ?? []).some((m) => m.email === e)) throw new DomainError("NOT_ALLOWED");
    await this.opAssert(workshopId, "passwordReset", { email: e });
    // demo: no e-mail goes out, the request is only recorded
    this.resetRequests.push(e);
  }
  async opSetFeatureGrants(workshopId: string, keys: string[]) {
    await this.opAssert();
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w || keys.some((k) => !feature(k))) throw new DomainError("NOT_ALLOWED");
    await this.opAssert(workshopId, "featureGrants", keys);
    w.extraFeatures = [...new Set(keys)].sort();
    w.features = w.features.filter((f) => featureAvailable(w.plan, f, w.extraFeatures));
  }
  async opBulk(ids: string[], a: OpBulkAction) {
    await this.opAssert();
    if (a.action === "trialExtend") {
      // like public.op_extend_trials: the qualifying ones get op_extend_trial (logged each), the others are skipped
      if (!Number.isInteger(a.value.days) || a.value.days < 1 || a.value.days > TRIAL_EXTEND_MAX_DAYS || ids.length === 0) throw new DomainError("NOT_ALLOWED");
      let n = 0;
      for (const id of new Set(ids)) {
        if (!this.workshops.some((w) => w.id === id)) continue;
        const sub = this.subOf(id);
        const today = todayIso();
        if (!sub.trialUntil || !(sub.trialUntil >= today || readOnly({ ...sub, status: this.billingOf(id).status }, today))) continue;
        await this.opExtendTrial(id, a.value.days);
        n += 1;
      }
      return String(n);
    }
    const list = [...new Set(ids)].map((id) => this.workshops.find((w) => w.id === id));
    if (list.length === 0 || list.some((w) => !w)) throw new DomainError("NOT_ALLOWED");
    const ok =
      a.action === "plan" ? ["basic", "medium", "premium"].includes(a.value.plan)
      : a.action === "feature" ? !!feature(a.value.key) && typeof a.value.on === "boolean"
      : a.action === "status" ? MemoryStore.STATUSES.includes(a.value.status)
      : a.action === "extend" ? [1, 2, 3, 6, 12].includes(a.value.months)
      : a.action === "fee" ? a.value.monthlyFee === null || a.value.monthlyFee >= 0
      : a.action === "gift" ? Number.isInteger(a.value.credits) && a.value.credits > 0 && a.value.credits <= 100_000 && a.value.reason.trim().length > 0
      : a.value.tag.trim().length >= 1 && a.value.tag.trim().length <= 30;
    if (!ok) throw new DomainError("NOT_ALLOWED");
    const batch = newId();
    for (const w of list as Workshop[]) {
      const b = this.billingOf(w.id);
      const x = this.billingExtra(w.id);
      let from: unknown = null;
      if (a.action === "plan") {
        if (w.plan === a.value.plan) continue;
        from = w.plan;
        (w as { plan: Plan }).plan = a.value.plan;
        if (this.subs.get(w.id)) this.subs.get(w.id)!.plan = a.value.plan;
        w.features = w.features.filter((f) => featureAvailable(w.plan, f, w.extraFeatures));
      } else if (a.action === "feature") {
        const has = w.features.includes(a.value.key);
        if (has === a.value.on || (a.value.on && !featureAvailable(w.plan, a.value.key, w.extraFeatures))) continue;
        w.features = a.value.on ? [...w.features, a.value.key] : w.features.filter((f) => f !== a.value.key);
      } else if (a.action === "status") {
        if (b.status === a.value.status) continue;
        from = b.status;
        b.status = a.value.status;
      } else if (a.action === "extend") {
        from = b.paidUntil;
        b.paidUntil = addMonths(b.paidUntil ?? todayIso(), a.value.months);
      } else if (a.action === "fee") {
        from = b.monthlyFee;
        b.monthlyFee = a.value.monthlyFee === null ? null : Math.round(a.value.monthlyFee);
      } else if (a.action === "gift") {
        this.aiGifts.push({ workshopId: w.id, at: now(), amount: Math.ceil((a.value.credits * 2000) / 150), reason: a.value.reason.trim() });
        this.addGrant(w.id, "gift", a.value.credits);
      } else if (a.action === "tagAdd") {
        const tag = a.value.tag.trim();
        if (x.tags.includes(tag)) continue;
        x.tags = MemoryStore.sortTags([...x.tags, tag]);
      } else {
        const tag = a.value.tag.trim();
        if (!x.tags.includes(tag)) continue;
        x.tags = x.tags.filter((t) => t !== tag);
      }
      this.billing.set(w.id, b);
      this.extras.set(w.id, x);
      await this.opAssert(w.id, a.action === "gift" ? "aiGift" : a.action, { from, value: a.value, batch, count: ids.length });
    }
    return batch;
  }
  async opLog(workshopId: string) {
    await this.opAssert();
    return this.opLogs.filter((l) => l.workshopId === workshopId).map(({ workshopId: _w, ...l }) => l);
  }
  // ---- shared type tree (shared by every workshop of this store) ----
  private types: (VehicleTypeNode & { parentId: string | null; attributes: { engineCode?: string; powerKw?: number | null } })[] = [];
  private facts: { id: string; typeId: string; field: string; value: string; sourceUrl: string | null; previewUrl?: string | null; origin: VehicleFact["origin"]; at: number; createdBy?: string | null }[] = [];
  private votes: { factId: string; userId: string; workshopId: string; agrees: boolean; at?: string }[] = [];
  private ownVehicle(id: string): Vehicle {
    const v = this.vehicles.get(id);
    if (!v || !this.myRole(v.workshopId)) throw new DomainError("NOT_ALLOWED");
    return v;
  }
  private static key = (s: string | null | undefined) => (s ?? "").trim().replace(/\s+/g, " ").toUpperCase();
  private node(parentId: string | null, level: VehicleTypeNode["level"], name: string, attributes = {}) {
    const k = MemoryStore.key(name);
    const found = this.types.find((n) => n.parentId === parentId && n.level === level && MemoryStore.key(n.name) === k);
    if (found) return found.id;
    const n = { id: newId(), parentId, level, name: name.trim(), attributes };
    this.types.push(n);
    return n.id;
  }
  async vehicleTypeSuggest(vehicleId: string): Promise<VehicleTypeSuggestion[]> {
    const v = this.ownVehicle(vehicleId);
    const codes = vehicleEngineCodes(v.engineCode);
    const out: VehicleTypeSuggestion[] = [];
    for (const e of this.types.filter((n) => n.level === "engine")) {
      const md = this.types.find((n) => n.id === e.parentId)!;
      const mk = this.types.find((n) => n.id === md.parentId)!;
      if (MemoryStore.key(mk.name) !== MemoryStore.key(v.make)) continue;
      const codeHit = vehicleEngineCodes(e.attributes.engineCode).some((c) => codes.includes(c));
      const kwHit = e.attributes.powerKw != null && e.attributes.powerKw === v.powerKw;
      if (!codeHit && !kwHit) continue;
      const score = (MemoryStore.key(md.name) === MemoryStore.key(vehicleModelName(v.model ?? "")) ? 2 : 0) + (kwHit ? 2 : 0) + (codeHit ? 3 : 0);
      out.push({ id: e.id, make: mk.name, model: md.name, engine: e.name, score });
    }
    return out.sort((a, b) => b.score - a.score).slice(0, 10);
  }
  async vehicleTypeChildren(parentId: string | null): Promise<VehicleTypeNode[]> {
    if (!this.user) throw new DomainError("NOT_ALLOWED");
    return this.types
      .filter((n) => n.parentId === parentId)
      .sort((a, b) => MemoryStore.key(a.name).localeCompare(MemoryStore.key(b.name)))
      .map((n) => ({ id: n.id, level: n.level, name: n.name }));
  }
  /** As public.vehicle_type_match: the given data in place of the vehicle's own; reads only. */
  async vehicleTypeMatch(vehicleId: string, data: { make?: string | null; model?: string | null; engineCode?: string | null; powerKw?: number | null }): Promise<VehicleTypeMatch[]> {
    const v = this.ownVehicle(vehicleId);
    // as public.vehicle_name_fold: no accents, no case
    const fold = (x: string | null | undefined) => MemoryStore.key(x).normalize("NFD").replace(/\p{M}/gu, "").replace(/Đ/g, "D").replace(/Ł/g, "L");
    const make = fold(data.make?.trim() || v.make);
    const model = fold(vehicleModelName(data.model?.trim() || v.model || ""));
    const codes = vehicleEngineCodes(data.engineCode?.trim() || v.engineCode);
    const kw = data.powerKw ?? v.powerKw ?? null;
    if (!make) return [];
    const out: VehicleTypeMatch[] = [];
    const fits = (e: (typeof this.types)[number]) =>
      vehicleEngineCodes(e.attributes.engineCode).some((c) => codes.includes(c)) || (e.attributes.powerKw != null && e.attributes.powerKw === kw);
    for (const md of this.types.filter((n) => n.level === "model")) {
      const mk = this.types.find((n) => n.id === md.parentId)!;
      if (fold(mk.name) !== make) continue;
      const engines = this.types.filter((n) => n.parentId === md.id && n.level === "engine" && fits(n));
      for (const e of engines) {
        const score = (model && fold(md.name) === model ? 2 : 0) + (e.attributes.powerKw != null && e.attributes.powerKw === kw ? 2 : 0) + (vehicleEngineCodes(e.attributes.engineCode).some((c) => codes.includes(c)) ? 3 : 0);
        out.push({ id: e.id, level: "engine", make: mk.name, model: md.name, engine: e.name, score });
      }
      if (engines.length === 0 && model && fold(md.name) === model) out.push({ id: md.id, level: "model", make: mk.name, model: md.name, engine: null, score: 1 });
    }
    return out.sort((a, b) => b.score - a.score || a.model.localeCompare(b.model)).slice(0, 10);
  }
  async serviceHistory(vehicleId: string): Promise<ServiceHistoryOrder[]> {
    this.ownVehicle(vehicleId);
    return [...this.orders.values()]
      .filter((o) => o.vehicleId === vehicleId && o.status === "closed")
      .sort((a, b) => (b.closedAt ?? "").localeCompare(a.closedAt ?? ""))
      .map((o) => ({
        id: o.id, number: o.number, status: o.status, closedAt: o.closedAt, odometerKm: o.odometerKm,
        items: [...this.items.values()].filter((i) => i.workOrderId === o.id).sort((a, b) => a.sortOrder - b.sortOrder).map((i) => this.priced(o.workshopId, i)),
      }));
  }
  async setVehicleType(vehicleId: string, typeId?: string) {
    const v = this.ownVehicle(vehicleId);
    let id = typeId;
    if (id) {
      if (!this.types.some((n) => n.id === id && (n.level === "engine" || n.level === "model"))) throw new DomainError("NOT_ALLOWED");
    } else {
      if (!v.make?.trim() || !v.model?.trim() || vehicleEngineCodes(v.engineCode).length === 0) throw new DomainError("VEHICLE_TYPE_DATA_MISSING");
      const mk = this.node(null, "make", v.make);
      const md = this.node(mk, "model", vehicleModelName(v.model));
      id = this.node(md, "engine", vehicleEngineLabel(v.model, v.engineCode, v.powerKw), { engineCode: vehicleEngineCodes(v.engineCode).join(", "), powerKw: v.powerKw ?? null });
    }
    this.vehicles.set(v.id, { ...v, typeId: id });
    return id;
  }
  async clearVehicleType(vehicleId: string) {
    const v = this.ownVehicle(vehicleId);
    this.vehicles.set(v.id, { ...v, typeId: null });
  }
  async vehicleTypePath(typeId: string): Promise<VehicleTypeNode[]> {
    const path: VehicleTypeNode[] = [];
    let n = this.types.find((x) => x.id === typeId);
    while (n) {
      path.unshift({ id: n.id, level: n.level, name: n.name });
      const parent: string | null = n.parentId;
      n = this.types.find((x) => x.id === parent);
    }
    return path;
  }
  /** Test helper: a fact found by the AI (the edge function writes these). */
  addAiVehicleFact(typeId: string, field: string, value: string, sourceUrl: string | null = null, previewUrl: string | null = null) {
    const id = newId();
    this.facts.push({ id, typeId, field, value, sourceUrl, previewUrl, origin: "ai", at: this.facts.length });
    return id;
  }
  async vehicleDataSheet(vehicleId: string): Promise<VehicleFact[]> {
    const v = this.ownVehicle(vehicleId);
    const me = this.user?.id;
    return this.facts
      .filter((f) => f.typeId === v.typeId)
      .map((f) => {
        const vs = this.votes.filter((x) => x.factId === f.id);
        const c = vs.filter((x) => x.agrees).length;
        const r = vs.length - c;
        const cw = new Set(vs.filter((x) => x.agrees).map((x) => x.workshopId)).size;
        const important = IMPORTANT_FACTS.includes(f.field);
        return {
          id: f.id, field: f.field, value: f.value, sourceUrl: f.sourceUrl, previewUrl: f.previewUrl ?? null, origin: f.origin, at: f.at,
          confirms: c, rejects: r, percent: c + r === 0 ? null : Math.round((100 * c) / (c + r)),
          confirmed: (important ? c >= 3 && cw >= 2 : c >= 2) && c > r,
          myVote: vs.find((x) => x.userId === me)?.agrees ?? null,
        };
      })
      .filter((f) => !(f.rejects >= 3 && f.rejects > f.confirms))
      .sort((a, b) => a.field.localeCompare(b.field) || b.confirms - b.rejects - (a.confirms - a.rejects) || a.at - b.at)
      .map(({ at: _at, ...f }) => f);
  }
  async addVehicleFact(vehicleId: string, field: string, value: string, sourceUrl?: string) {
    const v = this.ownVehicle(vehicleId);
    if (!v.typeId) throw new DomainError("VEHICLE_TYPE_MISSING");
    // the fuse sheet and the timing sheet come only from the AI lookup, with their sources
    if (field === "fuseTable" || field === "timingSheet") throw new DomainError("NOT_ALLOWED");
    if (!value.trim()) throw new DomainError("REQUIRED_MISSING");
    const id = newId();
    this.facts.push({ id, typeId: v.typeId, field, value: value.trim(), sourceUrl: sourceUrl?.trim() || null, origin: "mechanic", at: this.facts.length, createdBy: this.user!.id });
    this.votes.push({ factId: id, userId: this.user!.id, workshopId: v.workshopId, agrees: true, at: now() });
    this.rewardNote(v.workshopId, id);
  }
  async voteVehicleFact(vehicleId: string, factId: string, agrees: boolean | null) {
    const v = this.ownVehicle(vehicleId);
    if (!this.facts.some((f) => f.id === factId && f.typeId === v.typeId)) throw new DomainError("NOT_ALLOWED");
    const me = this.user!.id;
    this.votes = this.votes.filter((x) => !(x.factId === factId && x.userId === me));
    if (agrees !== null) {
      this.votes.push({ factId, userId: me, workshopId: v.workshopId, agrees, at: now() });
      // as public.vote_vehicle_fact: not on a fact the workshop's own member wrote
      const by = this.facts.find((f) => f.id === factId)?.createdBy ?? null;
      if (!by || !(this.members.get(v.workshopId) ?? []).some((m) => m.userId === by)) this.rewardNote(v.workshopId, factId);
    }
  }

  /** Test helpers: whether the AI is available, and what it "reads" from the next photo (null: unreadable). */
  aiOn = false;
  aiPhoto: VehiclePhotoResult | null = null;
  private filledTypes = new Set<string>();
  async aiAvailable() {
    return this.aiOn;
  }
  async readVehiclePhoto(workshopId: string, _image: string, _mediaType: string) {
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w || !this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    if (!featureOn(w, "aiVehiclePhoto")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (!this.aiPhoto) throw new DomainError("AI_UNREADABLE");
    return structuredClone(this.aiPhoto);
  }

  /** Test helpers: what NAV knows (by törzsszám), and what the AI "finds" for a company name (raw, as the AI answers). */
  navTaxpayers = new Map<string, TaxpayerData>();
  aiCompanies: { name: string; taxNumber: string; address?: string; source?: string }[] = [];
  async taxpayerLookup(workshopId: string, taxNumber: string) {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    const base = taxBase(taxNumber);
    if (!base) throw new DomainError("TAXPAYER_INVALID_NUMBER");
    if (!this.invoiceKeys.has(workshopId)) throw new DomainError("TAXPAYER_KEY_MISSING");
    const d = this.navTaxpayers.get(base);
    if (!d) throw new DomainError("TAXPAYER_NOT_FOUND");
    return { ...d, taxNumber: completeTaxNumber(d.taxNumber, taxNumber) };
  }
  async companyLookup(workshopId: string, name: string, _city?: string | null) {
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w || !this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    if (!featureOn(w, "aiVehiclePhoto")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (!this.aiOn) throw new DomainError("AI_FAILED");
    if (!isCompanyName(name)) throw new DomainError("NOT_ALLOWED");
    const keySet = this.invoiceKeys.has(workshopId);
    const candidates: CompanyCandidate[] = [];
    for (const c of cleanCandidates({ candidates: this.aiCompanies })) {
      const nav = this.navTaxpayers.get(c.taxNumber.replace(/\D/g, "").slice(0, 8));
      const checked = applyVerification(c, keySet ? (nav ? { ok: true, valid: true, data: nav } : { ok: true, valid: false }) : null);
      if (checked) candidates.push(checked);
    }
    candidates.sort((a, b) => Number(b.verified) - Number(a.verified));
    return { candidates, keySet };
  }
  async fillDataSheet(vehicleId: string) {
    const v = this.ownVehicle(vehicleId);
    const w = this.workshops.find((x) => x.id === v.workshopId)!;
    if (!featureOn(w, "aiDataSheet")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (!v.typeId) throw new DomainError("VEHICLE_TYPE_MISSING");
    if (this.types.find((n) => n.id === v.typeId)?.level !== "engine") throw new DomainError("VEHICLE_ENGINE_MISSING");
    if (this.filledTypes.has(v.typeId)) throw new DomainError("AI_ALREADY_FILLED");
    this.filledTypes.add(v.typeId);
    this.addAiVehicleFact(v.typeId, "oilCapacity", "3,7 l szűrővel", "https://example.com/olaj");
    this.addAiVehicleFact(v.typeId, "gearboxManual", "2,0 l 75W-80 · csere nincs előírva", "https://example.com/valto");
    this.addAiVehicleFact(v.typeId, "maintenance", "Levegőszűrő: 30 000 km / 2 év", "https://example.com/terv");
    this.addAiVehicleFact(v.typeId, "diagram", "Kezelési útmutató (PDF)", "https://example.com/kezelesi.pdf");
    return 4;
  }
  /** Test helper: the AI's answer to the next question. */
  aiAnswer: { answer: string; facts: { value: string; source: string | null }[] } | null = {
    answer: "A főtengely csavar meghúzási nyomatéka 85 Nm, utána 90°.",
    facts: [{ value: "Főtengely csavar: 85 Nm + 90°", source: "https://example.com/nyomatek" }],
  };
  /** Test helper: what a pack lookup "finds" (rowKey: value). */
  aiPackRows: Record<string, string[]> = { timing: ["timingKind: Fogasszíj", "timingInterval: 150 000 km / 10 év"] };
  async aiPack(vehicleId: string, pack: string) {
    const v = this.ownVehicle(vehicleId);
    this.noteQuestion(v, `pack:${pack}`);
    const def = AI_PACKS.find((p) => p.key === pack);
    const have = def && this.facts.some((f) => f.typeId === v.typeId && f.field === (def.sheetField ?? def.field));
    if (have || !def || !v.typeId) return { ...(await this.aiPackRun(vehicleId, pack)), credits: 0 };
    // paid in credits, given back when nothing was found (as ai_lookup_start / ai_lookup_finish)
    const lookup = this.takeCredits(v.workshopId, creditCost(this.creditSettings.costs, "pack", pack));
    const r = await this.aiPackRun(vehicleId, pack).catch((e) => { this.refund(lookup); throw e; });
    if (r.cached || r.facts.length === 0) {
      this.refund(lookup);
      return { ...r, credits: lookup.status === "refunded" ? 0 : lookup.credits };
    }
    return { ...r, credits: lookup.credits };
  }
  private async aiPackRun(vehicleId: string, pack: string) {
    const v = this.ownVehicle(vehicleId);
    const w = this.workshops.find((x) => x.id === v.workshopId)!;
    if (!featureOn(w, "aiDataSheet")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (!v.typeId) throw new DomainError("VEHICLE_TYPE_MISSING");
    const def = AI_PACKS.find((p) => p.key === pack);
    if (!def) throw new DomainError("NOT_ALLOWED");
    if (def.sheetField && this.aiTimingSheet) {
      // the timing pack: the rows and a diagram (step 1), then the own timing sheet (step 2);
      // rows from before: only the sheet is written, from the known diagram (cheaper)
      const sheet = this.facts.filter((f) => f.typeId === v.typeId && f.field === def.sheetField);
      if (sheet.length) return { cached: true, charged: 0, facts: sheet.map((f) => ({ value: f.value, source: f.sourceUrl })) };
      const before = this.facts.some((f) => f.typeId === v.typeId && f.field === def.field);
      if (!before) {
        for (const r of this.aiPackRows[pack] ?? []) this.addAiVehicleFact(v.typeId, def.field, r, "https://example.com/csomag");
        this.addAiVehicleFact(v.typeId, "diagram", `${pack}: Vezérlés jelölések és rögzítési pontok`, "https://www.example.com/vezerles/vw-19-tdi-pd.pdf");
      }
      const { torques, ...rest } = this.aiTimingSheet;
      const value = JSON.stringify({ ...rest, torques: torques.map((x) => [x.what, x.value]) });
      const source = this.aiTimingSheet.sources[0]?.url ?? null;
      this.addAiVehicleFact(v.typeId, def.sheetField, value, source);
      return { cached: false, charged: before ? 20 : 60, facts: [{ value, source }], knownSource: before };
    }
    const have = this.facts.filter((f) => f.typeId === v.typeId && f.field === def.field);
    if (have.length) return { cached: true, charged: 0, facts: have.map((f) => ({ value: f.value, source: f.sourceUrl })) };
    if (def.sheet) {
      // the fuse sheet: one table fact (its source the main source) and a diagram link
      const knownSource = await this.packKnownSource(vehicleId, pack);
      if (!this.aiFuseTable) return { cached: false, charged: knownSource ? 20 : 60, facts: [], knownSource };
      const value = JSON.stringify(this.aiFuseTable);
      const source = this.aiFuseTable.sources[0]?.url ?? null;
      this.addAiVehicleFact(v.typeId, def.field, value, source);
      if (!this.facts.some((f) => f.typeId === v.typeId && f.field === "diagram" && f.value.startsWith(`${pack}:`))) {
        this.addAiVehicleFact(v.typeId, "diagram", `${pack}: Biztosítéktábla kiosztás`, "https://example.com/biztositek");
      }
      return { cached: false, charged: knownSource ? 20 : 60, facts: [{ value, source }], knownSource };
    }
    const rows = this.aiPackRows[pack] ?? [];
    for (const r of rows) this.addAiVehicleFact(v.typeId, def.field, r, "https://example.com/csomag");
    return { cached: false, charged: rows.length ? 60 : 0, facts: rows.map((value) => ({ value, source: "https://example.com/csomag" })) };
  }
  /** Test helper: the timing sheet the timing lookup "writes" (null: only the rows are found). */
  aiTimingSheet: TimingSheet | null = DEMO_TIMING_SHEET;
  /** Test helper: the fuse table the fuse lookup "finds" (null: nothing found). */
  aiFuseTable: FuseTable | null = DEMO_FUSE_TABLE;
  /** As public.pack_known_sources: the type's own pack pictures, or a table of another type of the make. */
  async packKnownSource(vehicleId: string, pack: string) {
    const v = this.ownVehicle(vehicleId);
    if (!v.typeId) return false;
    const makeOf = (typeId: string) => {
      let n = this.types.find((x) => x.id === typeId);
      while (n?.parentId) {
        const parent: string = n.parentId;
        n = this.types.find((x) => x.id === parent);
      }
      return n?.id ?? null;
    };
    const make = makeOf(v.typeId);
    const rejected = (id: string) => {
      const vs = this.votes.filter((x) => x.factId === id);
      const r = vs.filter((x) => !x.agrees).length;
      return r >= 3 && r > vs.length - r;
    };
    return this.facts.some((f) =>
      !!f.sourceUrl && !rejected(f.id)
      && ((f.field === "diagram" && f.value.startsWith(`${pack}:`)) || (pack === "fuses" && (f.field === "fuseTable" || f.field === "pFuses")))
      && (f.typeId === v.typeId || (make !== null && makeOf(f.typeId) === make)));
  }
  async askVehicleData(vehicleId: string, question: string, item?: string) {
    const v = this.ownVehicle(vehicleId);
    const w = this.workshops.find((x) => x.id === v.workshopId)!;
    this.requireCan(w.id, "aiAsk");
    if (!featureOn(w, "aiDataSheet")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (!v.typeId) throw new DomainError("VEHICLE_TYPE_MISSING");
    const key = item && /^ask:[a-zA-Z]{2,40}$/.test(item) ? item : question.trim() ? questionItem(question) : null;
    if (!key && !question.trim()) throw new DomainError("NOT_ALLOWED");
    if (key) {
      this.noteQuestion(v, key);
      const kept = this.answers.get(`${v.typeId}|${key}|${w.language}`);
      if (kept) return { ...structuredClone(kept), cached: true, credits: 0 };
    }
    const lookup = this.takeCredits(v.workshopId, creditCost(this.creditSettings.costs, "question"));
    if (!this.aiAnswer) {
      this.refund(lookup);
      throw new DomainError("AI_FAILED");
    }
    for (const f of this.aiAnswer.facts) this.addAiVehicleFact(v.typeId, "custom", f.value, f.source);
    const answer = structuredClone(this.aiAnswer);
    if (answer.facts.length === 0) {
      this.refund(lookup);
      return { ...answer, cached: false, credits: lookup.status === "refunded" ? 0 : lookup.credits };
    }
    if (key) this.answers.set(`${v.typeId}|${key}|${w.language}`, structuredClone(answer));
    return { ...answer, cached: false, credits: lookup.credits };
  }
  async aiTopQuestions(vehicleId: string): Promise<TopQuestion[]> {
    const v = this.ownVehicle(vehicleId);
    const w = this.workshops.find((x) => x.id === v.workshopId)!;
    const model = v.typeId ? this.modelOf(v.typeId) : null;
    const min = this.creditSettings.topMinWorkshops;
    const rank = (rows: typeof this.questionLog) => {
      const by = new Map<string, { ws: Set<string>; n: number }>();
      for (const r of rows) {
        const x = by.get(r.item) ?? { ws: new Set(), n: 0 };
        x.ws.add(r.workshopId);
        x.n++;
        by.set(r.item, x);
      }
      return [...by.entries()].filter(([, x]) => x.ws.size >= min).sort((a, b) => b[1].ws.size - a[1].ws.size || b[1].n - a[1].n || a[0].localeCompare(b[0])).map(([i]) => i);
    };
    const out: TopQuestion[] = [];
    const add = (item: string, scope: TopQuestion["scope"]) => { if (out.length < 5 && !out.some((x) => x.item === item)) out.push({ item, scope, have: false }); };
    if (model) for (const i of rank(this.questionLog.filter((r) => r.modelId === model))) add(i, "model");
    for (const i of rank(this.questionLog)) add(i, "all");
    for (const i of TOP_QUESTION_SEED) add(i, "seed");
    return out.map((x) => ({ ...x, have: x.item.startsWith("pack:")
      ? (() => { const d = AI_PACKS.find((p) => p.key === x.item.slice(5)); return !!d && this.facts.some((f) => f.typeId === v.typeId && f.field === (d.sheetField ?? d.field)); })()
      : this.answers.has(`${v.typeId}|${x.item}|${w.language}`) }));
  }
  async typeFilled(vehicleId: string) {
    const v = this.ownVehicle(vehicleId);
    return !!v.typeId && this.filledTypes.has(v.typeId);
  }

  private seatOverrides = new Map<string, number | null>();
  private static SEATS: Record<Plan, number> = { basic: 2, medium: 5, premium: 10 };
  private seatLimit(workshopId: string) {
    const w = this.workshops.find((x) => x.id === workshopId);
    return Math.min((this.seatOverrides.get(workshopId) ?? (w ? MemoryStore.SEATS[w.plan] : 100000)) + (this.subs.get(workshopId)?.extraSeats ?? 0), 100000);
  }
  async seats(workshopId: string) {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    return { used: (this.members.get(workshopId) ?? []).length, limit: this.seatLimit(workshopId) };
  }
  async opActivity(): Promise<OpActivity[]> {
    await this.opAssert();
    const month = new Date().toISOString().slice(0, 7);
    return this.workshops.map((w) => ({
      workshopId: w.id,
      workOrders: [...this.orders.values()].filter((o) => o.workshopId === w.id && o.receivedAt.startsWith(month)).length,
      vehicles: [...this.vehicles.values()].filter((v) => v.workshopId === w.id && v.createdAt.startsWith(month)).length,
      members: (this.members.get(w.id) ?? []).length,
      seatLimit: this.seatLimit(w.id),
    }));
  }
  // ---- operator statistics ----
  /** One workshop's figures in [a, b) (milliseconds); imported records are left out. */
  private statFigures(workshopId: string, a: number, b: number): OpStatFigures {
    const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Date.now());
    const inside = (iso: string | null | undefined) => { const x = at(iso); return x >= a && x < b; };
    const sum = <T>(list: T[], f: (x: T) => number) => list.reduce((s, x) => s + f(x), 0);
    const all = [...this.orders.values()].filter((o) => o.workshopId === workshopId);
    const own = all.filter((o) => !o.legacyId);
    const closed = new Set(own.filter((o) => o.status === "closed" && o.closedAt && inside(o.closedAt)).map((o) => o.id));
    const ids = new Set(all.map((o) => o.id));
    const quotes = this.quotes.filter((x) => ids.has(x.workOrderId) && inside(x.createdAt));
    const answered = quotes.filter((x) => x.answeredAt);
    const ai = this.aiLog.filter((x) => x.workshopId === workshopId && inside(x.at));
    const cost = sum(ai.filter((x) => !x.shared), (x) => x.costHuf);
    const shared = sum(ai.filter((x) => x.shared), (x) => x.costHuf);
    const charged = sum(ai, (x) => x.chargedHuf);
    const packs = this.aiBought.filter((x) => x.workshopId === workshopId && inside(x.at));
    const sms = this.sentMessages.filter((m) => m.workshopId === workshopId && (m.channel === "sms" || m.channel === "smsTest") && inside(m.at));
    const members = this.members.get(workshopId) ?? [];
    return {
      users: members.length,
      activeUsers: members.filter((m) => { const s = this.signIns.get(m.userId); return !!s && inside(s); }).length,
      workOrdersCreated: own.filter((o) => inside(o.receivedAt)).length,
      workOrdersClosed: closed.size,
      revenue: sum([...this.items.values()].filter((i) => closed.has(i.workOrderId) && i.approval !== "declined"), (i) => i.gross),
      invoicesIssued: [...this.invoices.entries()].filter(([wo, inv]) => ids.has(wo) && inv.status === "issued" && inside(inv.issuedAt)).length,
      quotesSent: quotes.length,
      quotesAnswered: answered.length,
      quoteItems: sum(answered, (x) => x.itemIds.length),
      quoteItemsApproved: sum(answered, (x) => x.approvedIds.length),
      aiCalls: ai.length, aiCost: cost, aiSharedCost: shared, aiCharged: charged, aiMargin: charged - cost - shared,
      packsBought: packs.length, packsHuf: sum(packs, (x) => x.amount),
      smsCount: sms.length, smsAmount: sum(sms, (m) => m.price),
      vehiclesNew: [...this.vehicles.values()].filter((v) => v.workshopId === workshopId && !v.legacyId && inside(v.createdAt)).length,
      customersNew: [...this.customers.values()].filter((c) => c.workshopId === workshopId && !c.legacyId && inside(c.createdAt)).length,
      knowledgeVotes: this.votes.filter((v) => v.workshopId === workshopId && inside(v.at)).length,
    };
  }
  private static dayRange(from: string, to: string) {
    const a = new Date(`${from}T00:00:00`);
    const b = new Date(`${to}T00:00:00`);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) throw new DomainError("NOT_ALLOWED");
    b.setDate(b.getDate() + 1);
    return { a: a.getTime(), b: b.getTime(), days: Math.round((b.getTime() - a.getTime()) / 86400000) };
  }
  private static day(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  private statTotals(from: string, to: string): OpStatTotals {
    const { a, b } = MemoryStore.dayRange(from, to);
    const rows = this.workshops.map((w) => this.statFigures(w.id, a, b));
    const total = Object.fromEntries((Object.keys(rows[0] ?? this.statFigures("", a, b)) as (keyof OpStatFigures)[]).map((k) => [k, rows.reduce((s, r) => s + r[k], 0)])) as OpStatFigures;
    const created = (id: string) => Date.parse(this.workshopCreated.get(id) ?? now());
    const facts = this.facts.map((f) => {
      const vs = this.votes.filter((v) => v.factId === f.id && Date.parse(v.at ?? now()) < b);
      const c = vs.filter((v) => v.agrees).length;
      const cw = new Set(vs.filter((v) => v.agrees).map((v) => v.workshopId)).size;
      return ((IMPORTANT_FACTS.includes(f.field) ? c >= 3 && cw >= 2 : c >= 2) && c > vs.length - c);
    });
    const userIds = new Set([...this.members.values()].flat().map((m) => m.userId));
    return {
      ...total,
      users: userIds.size,
      activeUsers: [...userIds].filter((id) => { const s = this.signIns.get(id); return !!s && Date.parse(s) >= a && Date.parse(s) < b; }).length,
      workshopsTotal: this.workshops.filter((w) => created(w.id) < b).length,
      workshopsActive: rows.filter((r) => r.activeUsers > 0 || r.workOrdersCreated > 0 || r.aiCalls > 0).length,
      workshopsNew: this.workshops.filter((w) => created(w.id) >= a && created(w.id) < b).length,
      knowledgeTypes: this.types.length,
      knowledgeFacts: this.facts.length,
      knowledgeConfirmed: facts.filter(Boolean).length,
      from, to,
    };
  }
  async opStatsOverview(from: string, to: string): Promise<OpStatsOverview> {
    await this.opAssert();
    const { days } = MemoryStore.dayRange(from, to);
    const pf = new Date(`${from}T00:00:00`);
    pf.setDate(pf.getDate() - days);
    const pt = new Date(`${from}T00:00:00`);
    pt.setDate(pt.getDate() - 1);
    return { current: this.statTotals(from, to), previous: this.statTotals(MemoryStore.day(pf), MemoryStore.day(pt)) };
  }
  async opStatsWorkshops(from: string, to: string): Promise<OpStatsWorkshop[]> {
    await this.opAssert();
    const { a, b } = MemoryStore.dayRange(from, to);
    return this.workshops
      .map((w) => {
        const members = this.members.get(w.id) ?? [];
        const bl = this.billing.get(w.id);
        const times = [
          ...[...this.orders.values()].filter((o) => o.workshopId === w.id).map((o) => o.closedAt ?? o.receivedAt),
          ...this.aiLog.filter((x) => x.workshopId === w.id).map((x) => x.at),
          ...members.map((m) => this.signIns.get(m.userId)).filter((x): x is string => !!x),
        ].sort();
        return {
          ...this.statFigures(w.id, a, b),
          workshopId: w.id, name: w.name, plan: w.plan, currency: w.currency, createdAt: this.workshopCreated.get(w.id) ?? now(),
          ownerEmail: members.find((m) => m.role === "owner")?.email ?? null,
          billingStatus: bl?.status ?? "trial", monthlyFee: bl?.monthlyFee ?? null,
          seatsUsed: members.length, seatLimit: this.seatLimit(w.id), lastActivity: times.at(-1) ?? null,
        };
      })
      .sort((x, y) => x.name.localeCompare(y.name));
  }
  async opStatsMonthly(months: number, workshopId?: string | null): Promise<OpStatsMonth[]> {
    await this.opAssert();
    if (!(months >= 1 && months <= 60)) throw new DomainError("NOT_ALLOWED");
    const d = new Date();
    const list = workshopId ? this.workshops.filter((w) => w.id === workshopId) : this.workshops;
    return Array.from({ length: months }, (_, i) => {
      const start = new Date(d.getFullYear(), d.getMonth() - (months - 1 - i), 1);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
      const rows = list.map((w) => this.statFigures(w.id, start.getTime(), end.getTime()));
      const s = (k: keyof OpStatFigures) => rows.reduce((x, r) => x + r[k], 0);
      return {
        month: MemoryStore.day(start), workOrders: s("workOrdersCreated"), closed: s("workOrdersClosed"), revenue: s("revenue"),
        aiCalls: s("aiCalls"), aiCost: s("aiCost"), aiSharedCost: s("aiSharedCost"), aiCharged: s("aiCharged"),
        smsCount: s("smsCount"), smsAmount: s("smsAmount"), activeUsers: s("activeUsers"),
      };
    });
  }
  async opSetSeats(workshopId: string, maxUsers: number | null) {
    await this.opAssert(workshopId, "seats", maxUsers);
    this.seatOverrides.set(workshopId, maxUsers);
  }

  private aiSettings: AiSettings = {
    markup: 2.5, vatRate: 27, usdHuf: 370, allowance: { basic: 1000, medium: 3000, premium: 10000 }, packs: [2000, 5000, 10000], alertShare: 0.3,
    freeKinds: ["vehiclePhoto", "dictation", "companyLookup"], freeMonthlyCalls: 300, ...structuredClone(AI_GUARD_DEFAULTS), usdHufChangedAt: now(),
  };
  private aiLog: { workshopId: string; at: string; costHuf: number; chargedHuf: number; shared: boolean }[] = [];
  private aiBought: { workshopId: string; at: string; amount: number; credits?: number }[] = [];
  private aiGifts: { workshopId: string; at: string; amount: number; reason: string }[] = [];
  /** Test helper: an AI call of the given real cost (forint), as the edge functions log it. */
  recordAiCall(workshopId: string, costHuf: number, shared = false) {
    this.aiLog.push({ workshopId, at: new Date().toISOString(), costHuf, shared, chargedHuf: shared ? 0 : Math.ceil(costHuf * this.aiSettings.markup * (1 + this.aiSettings.vatRate / 100)) });
  }
  // ---- Okos lekérdezés credits (the same rules as 20261220100000_okos_lekerdezes.sql) ----
  private creditSettings: CreditSettings = structuredClone(CREDIT_DEFAULTS);
  private creditOwn = new Map<string, { allowance: number | null; purchaseLimit: number | null }>();
  private grants: { id: string; workshopId: string; source: "purchase" | "gift" | "conversion"; credits: number; used: number; at: string }[] = [];
  /** Test helper: the real cost (forint) of the next lookups; a failed or empty one is refunded only below refundMaxCostHuf. */
  aiLookupCostHuf = 0;
  private lookups: { id: string; workshopId: string; at: string; credits: number; fromAllowance: number; fromGrants: { id: string; n: number }[]; status: "done" | "refunded" }[] = [];
  private rewardActions: { workshopId: string; factId: string; at: string }[] = [];
  private rewards = new Map<string, number>();
  private questionLog: { workshopId: string; typeId: string | null; modelId: string | null; item: string }[] = [];
  private answers = new Map<string, { answer: string; facts: { value: string; source: string | null }[] }>();
  private addGrant(workshopId: string, source: "purchase" | "gift" | "conversion", credits: number) {
    this.grants.push({ id: newId(), workshopId, source, credits, used: 0, at: new Date().toISOString() });
  }
  /** Test helper: the old forint credit, converted as the migration does. */
  convertHufCredit(workshopId: string, huf: number) {
    this.addGrant(workshopId, "conversion", hufToCredits(huf));
  }
  private modelOf(typeId: string): string | null {
    let n = this.types.find((x) => x.id === typeId);
    while (n && n.level !== "model" && n.parentId) {
      const parent: string = n.parentId;
      n = this.types.find((x) => x.id === parent);
    }
    return n?.level === "model" ? n.id : null;
  }
  private noteQuestion(v: Vehicle, item: string) {
    this.questionLog.push({ workshopId: v.workshopId, typeId: v.typeId ?? null, modelId: v.typeId ? this.modelOf(v.typeId) : null, item });
  }
  /** Test helper: an item asked by another workshop (the statistics of the five most asked). */
  noteOtherQuestion(workshopId: string, item: string, typeId: string | null = null) {
    this.questionLog.push({ workshopId, typeId, modelId: typeId ? this.modelOf(typeId) : null, item });
  }
  private rewardNote(workshopId: string, factId: string) {
    if (this.rewardActions.some((r) => r.workshopId === workshopId && r.factId === factId)) return;
    this.rewardActions.push({ workshopId, factId, at: now() });
    const month = now().slice(0, 7);
    const n = this.rewardActions.filter((r) => r.workshopId === workshopId && r.at.startsWith(month)).length;
    const key = `${workshopId}|${month}`;
    this.rewards.set(key, Math.max(this.rewards.get(key) ?? 0, rewardEarned(this.creditSettings, n)));
  }
  private creditBalance(workshopId: string) {
    const w = this.workshops.find((x) => x.id === workshopId);
    const sub = this.subOf(workshopId);
    const trial = inTrial(sub, todayIso());
    const allowance = this.creditOwn.get(workshopId)?.allowance ?? (trial ? this.creditSettings.trial : w ? this.creditSettings.allowance[w.plan] : 0);
    const month = now().slice(0, 7);
    const reward = this.rewards.get(`${workshopId}|${month}`) ?? 0;
    const mine = this.lookups.filter((l) => l.workshopId === workshopId && l.status !== "refunded" && (trial || l.at.startsWith(month)));
    const allowanceLeft = Math.max(allowance + reward - mine.reduce((a, l) => a + l.fromAllowance, 0), 0);
    const valid = this.grants.filter((g) => g.workshopId === workshopId && g.used < g.credits).sort((a, b) => a.at.localeCompare(b.at));
    const purchasedLeft = valid.reduce((a, g) => a + g.credits - g.used, 0);
    return { trial, allowance, reward, allowanceLeft, purchasedLeft, available: allowanceLeft + purchasedLeft, valid,
      usedThisMonth: mine.filter((l) => l.at.startsWith(month)).reduce((a, l) => a + l.credits, 0) };
  }
  private takeCredits(workshopId: string, credits: number) {
    const b = this.creditBalance(workshopId);
    if (b.available < credits) throw new DomainError(b.available > 0 ? "AI_CREDIT_LOW" : "AI_CREDIT_EMPTY", String(credits));
    const fromAllowance = Math.min(credits, b.allowanceLeft);
    let need = credits - fromAllowance;
    const fromGrants: { id: string; n: number }[] = [];
    for (const g of b.valid) {
      if (need <= 0) break;
      const n = Math.min(need, g.credits - g.used);
      g.used += n;
      fromGrants.push({ id: g.id, n });
      need -= n;
    }
    const l = { id: newId(), workshopId, at: now(), credits, fromAllowance, fromGrants, status: "done" as "done" | "refunded" };
    this.lookups.push(l);
    return l;
  }
  /** As public.ai_lookup_finish for a failed or empty lookup: refunded only when its real cost was small. */
  private refund(l: { fromGrants: { id: string; n: number }[]; status: "done" | "refunded" }) {
    if (l.status === "refunded" || this.aiLookupCostHuf >= this.creditSettings.refundMaxCostHuf) return;
    for (const x of l.fromGrants) {
      const g = this.grants.find((y) => y.id === x.id);
      if (g) g.used = Math.max(g.used - x.n, 0);
    }
    l.status = "refunded";
  }
  /** Test helper: a lookup of this many credits, as the edge function takes it. */
  spendCredits(workshopId: string, credits: number) {
    this.takeCredits(workshopId, credits);
  }
  async aiCredits(workshopId: string): Promise<AiCredits> {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    const b = this.creditBalance(workshopId);
    const month = now().slice(0, 7);
    const vat = this.aiSettings.vatRate;
    return {
      allowance: b.allowance, reward: b.reward, usedThisMonth: b.usedThisMonth, allowanceLeft: b.allowanceLeft, purchasedLeft: b.purchasedLeft,
      available: b.available, grants: b.valid.map((g) => ({ credits: g.credits - g.used, grantedAt: g.at, source: g.source })),
      trial: b.trial, blocked: null,
      packs: this.creditSettings.packs.map((p) => ({ ...p, grossHuf: Math.round(p.netHuf * (1 + vat / 100)) })),
      purchaseLimit: this.creditOwn.get(workshopId)?.purchaseLimit ?? this.creditSettings.monthlyPurchaseLimit,
      boughtThisMonth: this.grants.filter((g) => g.workshopId === workshopId && g.source === "purchase" && g.at.startsWith(month)).reduce((a, g) => a + g.credits, 0),
      freeCallsLeft: 0, planCredits: { ...this.creditSettings.allowance },
      costs: { ...this.creditSettings.costs }, rewardEvery: this.creditSettings.rewardEvery, rewardCredits: this.creditSettings.rewardCredits,
      rewardMonthlyMax: this.creditSettings.rewardMonthlyMax,
      rewardActions: this.rewardActions.filter((r) => r.workshopId === workshopId && r.at.startsWith(month)).length,
    };
  }
  async buyAiCredits(workshopId: string, credits: number) {
    const role = this.myRole(workshopId);
    const pack = this.creditSettings.packs.find((p) => p.credits === credits);
    if ((role !== "owner" && role !== "manager") || !pack) throw new DomainError("NOT_ALLOWED");
    const sub = this.subOf(workshopId);
    const c = await this.aiCredits(workshopId);
    const refusal = creditPurchaseRefusal({
      trial: inTrial(sub, todayIso()),
      readOnly: readOnly({ ...sub, status: this.billingOf(workshopId).status }, todayIso()),
      status: this.billingOf(workshopId).status,
      boughtThisMonth: c.boughtThisMonth,
      limit: c.purchaseLimit,
    }, credits);
    if (refusal) throw new DomainError(refusal);
    // the gross price goes on the monthly bill
    this.aiBought.push({ workshopId, at: new Date().toISOString(), amount: Math.round(pack.netHuf * (1 + this.aiSettings.vatRate / 100)), credits });
    this.addGrant(workshopId, "purchase", credits);
  }
  async opCreditPurchases(monthStart?: string) {
    await this.opAssert();
    const month = (monthStart ?? new Date().toISOString()).slice(0, 7);
    return this.aiBought.filter((b) => b.at.startsWith(month)).map((b, i) => ({ id: `p${i}`, workshopId: b.workshopId, boughtAt: b.at, credits: b.credits ?? hufToCredits(b.amount), grossHuf: b.amount, billedAt: null }));
  }
  async opAiCredits(monthStart?: string): Promise<OpAiCredits[]> {
    await this.opAssert();
    const month = (monthStart ?? new Date().toISOString()).slice(0, 7);
    return this.workshops.map((w) => {
      const b = this.creditBalance(w.id);
      const l = this.lookups.filter((x) => x.workshopId === w.id && x.at.startsWith(month));
      const g = (source: string, all = false) => this.grants.filter((x) => x.workshopId === w.id && x.source === source && (all || x.at.startsWith(month))).reduce((a, x) => a + x.credits, 0);
      return {
        workshopId: w.id, ownAllowance: this.creditOwn.get(w.id)?.allowance ?? null, ownPurchaseLimit: this.creditOwn.get(w.id)?.purchaseLimit ?? null,
        allowance: b.allowance, available: b.available, purchasedLeft: b.purchasedLeft,
        used: l.filter((x) => x.status !== "refunded").reduce((a, x) => a + x.credits, 0),
        refunded: l.filter((x) => x.status === "refunded").reduce((a, x) => a + x.credits, 0),
        lookups: l.filter((x) => x.status !== "refunded").length,
        bought: g("purchase"), gifted: g("gift"), giftedTotal: g("gift", true), rewarded: this.rewards.get(`${w.id}|${month}`) ?? 0,
      };
    });
  }
  async opSetCreditSettings(c: CreditSettings) {
    await this.opAssert();
    const int = (n: unknown, min = 0) => typeof n === "number" && Number.isInteger(n) && n >= min;
    const ok = int(c.costs?.simple, 1) && int(c.costs?.heavy, 1) && int(c.costs?.helper, 1) && Object.values(c.allowance ?? {}).every((n) => int(n))
      && Array.isArray(c.packs) && c.packs.length > 0 && c.packs.every((p) => int(p.credits, 1) && typeof p.netHuf === "number" && p.netHuf > 0)
      && int(c.trial) && int(c.monthlyPurchaseLimit) && int(c.refundMaxCostHuf) && int(c.rewardEvery, 1) && int(c.rewardCredits) && int(c.rewardMonthlyMax) && int(c.topMinWorkshops, 2);
    if (!ok) throw new DomainError("NOT_ALLOWED");
    this.creditSettings = structuredClone(c);
  }
  async opAiToday(): Promise<OpAiToday> {
    await this.opAssert();
    // the demo log keeps UTC times: today by the same clock
    const day = now().slice(0, 10);
    const u = this.aiLog.filter((x) => x.at.slice(0, 10) === day);
    return { day, costHuf: u.reduce((a, x) => a + x.costHuf, 0), calls: u.length, capHuf: this.aiSettings.dailyCostCapHuf ?? AI_GUARD_DEFAULTS.dailyCostCapHuf, alertAt: null };
  }
  async opAiLimits(): Promise<OpAiLimit[]> {
    await this.opAssert();
    const day = now().slice(0, 10);
    const month = day.slice(0, 7);
    return this.workshops.map((w) => ({
      workshopId: w.id,
      dailyCapHuf: this.aiOwnLimits.get(w.id)?.dailyCapHuf ?? null,
      purchaseLimitHuf: this.aiOwnLimits.get(w.id)?.purchaseLimitHuf ?? null,
      todayCostHuf: this.aiLog.filter((x) => x.workshopId === w.id && x.at.slice(0, 10) === day).reduce((a, x) => a + x.costHuf, 0),
      monthBoughtHuf: this.aiBought.filter((b) => b.workshopId === w.id && b.at.startsWith(month)).reduce((a, b) => a + b.amount, 0),
    }));
  }
  async opAiUsage(monthStart?: string): Promise<OpAiUsage[]> {
    await this.opAssert();
    const month = (monthStart ?? new Date().toISOString()).slice(0, 7);
    return this.workshops.map((w) => {
      const u = this.aiLog.filter((x) => x.workshopId === w.id && x.at.startsWith(month));
      return {
        workshopId: w.id,
        calls: u.length,
        costHuf: u.filter((x) => !x.shared).reduce((a, x) => a + x.costHuf, 0),
        chargedHuf: u.reduce((a, x) => a + x.chargedHuf, 0),
        sharedCostHuf: u.filter((x) => x.shared).reduce((a, x) => a + x.costHuf, 0),
        boughtHuf: this.aiBought.filter((b) => b.workshopId === w.id && b.at.startsWith(month)).reduce((a, b) => a + b.amount, 0),
      };
    });
  }
  async opAiSettings() {
    await this.opAssert();
    return structuredClone({ ...this.aiSettings, credits: this.creditSettings });
  }
  async opSetAiSettings(s: AiSettings) {
    await this.opAssert();
    const ok = s.markup >= 1 && s.vatRate >= 0 && s.vatRate <= 100 && s.usdHuf > 0 && s.alertShare > 0 && s.packs.length > 0 && s.packs.every((p) => p > 0) && Object.values(s.allowance).every((a) => a >= 0) && s.freeMonthlyCalls >= 0;
    if (!ok) throw new DomainError("NOT_ALLOWED");
    const changedAt = s.usdHuf !== this.aiSettings.usdHuf ? now() : this.aiSettings.usdHufChangedAt;
    this.aiSettings = { ...this.aiSettings, ...structuredClone(s), usdHufChangedAt: changedAt };
  }
  async clearSmsPhone(workshopId: string) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_OWNER");
    this.smsPhones.delete(workshopId);
  }
  async sendQuote(workOrderId: string, channel: QuoteChannel, message: QuoteMessage) {
    const o = this.orders.get(workOrderId);
    const c = o?.customerId ? this.customers.get(o.customerId) : undefined;
    const open = o && [...this.quotes].reverse().find((q) => q.workOrderId === o.id);
    if (!o || !c || !this.can(o.workshopId, "quote")) throw new DomainError("NOT_ALLOWED");
    if (!open || open.answeredAt || !message.link.endsWith(open.token)) throw new DomainError("QUOTE_NOT_FOUND");
    if (channel === "sms") this.smsReady(o.workshopId);
    const to = channel === "email" ? c.email : c.mobile || c.phone;
    if (!to) throw new DomainError("SEND_FAILED");
    const price = channel === "sms" ? this.smsPrice(o.workshopId) : 0;
    this.sentMessages.push({ workshopId: o.workshopId, workOrderId, channel, to, price, at: now(), ...message });
    return { to, price };
  }
  // ---- "car ready" notices (like public.ready_notices) ----
  private readyList: (ReadyNotice & { workOrderId: string; workshopId: string; currency: string; hours: string | null })[] = [];
  async createReadyNotice(workOrderId: string, amount: number | null, hours: string | null) {
    const o = this.orders.get(workOrderId);
    if (!o || !this.can(o.workshopId, "notice")) throw new DomainError("NOT_ALLOWED");
    if (amount !== null && amount < 0) throw new DomainError("INVALID_PRICE");
    const w = this.workshops.find((x) => x.id === o.workshopId)!;
    const token = Array.from(globalThis.crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");
    this.readyList.push({
      id: newId(), token, createdAt: now(), expiresAt: new Date(Date.now() + 30 * 864e5).toISOString(), channels: [], emailTo: null, emailSentAt: null,
      smsTo: null, smsSentAt: null, amount, workOrderId, workshopId: o.workshopId, currency: w.currency, hours: hours?.trim() || null,
    });
    return token;
  }
  async sendReadyNotice(workOrderId: string, channel: QuoteChannel, message: QuoteMessage) {
    const o = this.orders.get(workOrderId);
    const c = o?.customerId ? this.customers.get(o.customerId) : undefined;
    if (!o || !c || !this.can(o.workshopId, "notice") || o.status === "closed") throw new DomainError("NOT_ALLOWED");
    const last = [...this.readyList].reverse().find((n) => n.workOrderId === workOrderId);
    if (!last || last.expiresAt <= now() || !message.link.endsWith(`#ready/${last.token}`)) throw new DomainError("READY_NOT_FOUND");
    if (channel === "sms") {
      this.smsReady(o.workshopId);
      if (!message.text.includes(message.link)) throw new DomainError("NOT_ALLOWED");
    }
    const to = channel === "email" ? c.email : c.mobile || c.phone;
    if (!to) throw new DomainError("SEND_FAILED");
    const price = channel === "sms" ? this.smsPrice(o.workshopId) : 0;
    this.sentMessages.push({ workshopId: o.workshopId, workOrderId, channel, to, price, at: now(), kind: "ready", ...message });
    if (!last.channels.includes(channel)) last.channels = [...last.channels, channel];
    if (channel === "email") Object.assign(last, { emailTo: to, emailSentAt: now() });
    else Object.assign(last, { smsTo: `…${to.replace(/\D/g, "").slice(-4)}`, smsSentAt: now() });
    return { to, price };
  }
  async readyNotices(workOrderId: string): Promise<ReadyNotice[]> {
    const o = this.orders.get(workOrderId);
    if (!o || !this.can(o.workshopId, "notice")) return [];
    return this.readyList.filter((n) => n.workOrderId === workOrderId).reverse()
      .map(({ id, token, createdAt, expiresAt, channels, emailTo, emailSentAt, smsTo, smsSentAt, amount }) => ({ id, token, createdAt, expiresAt, channels: [...channels], emailTo, emailSentAt, smsTo, smsSentAt, amount }));
  }
  async readyNoticeView(token: string): Promise<PublicReady> {
    const n = this.readyList.find((x) => x.token === token);
    if (!n) throw new DomainError("READY_NOT_FOUND");
    if (n.expiresAt <= now()) throw new DomainError("READY_EXPIRED");
    const o = this.orders.get(n.workOrderId)!;
    const w = this.workshops.find((x) => x.id === o.workshopId)!;
    const v = this.vehicles.get(o.vehicleId ?? "");
    const g = (k: string) => (typeof w.general[k] === "string" && String(w.general[k]).trim()) || null;
    return {
      workshopName: w.name, workshopPhone: g("workshopPhone"), address: siteAddressOf(w.general), contacts: workshopContactsOf(w.general), language: w.language, currency: n.currency,
      number: o.number, plateNumber: v?.plateNumber ?? null, plateCountry: v?.plateCountry ?? null,
      vehicle: v ? [v.make, v.model].filter(Boolean).join(" ") || null : null, customerName: this.customers.get(o.customerId ?? "")?.name ?? null,
      amount: n.amount, hours: n.hours, sentAt: n.createdAt, expiresAt: n.expiresAt,
    };
  }
  private followups = new Map<string, string | null>();
  private notices = new Map<string, string>();
  async vehicleRecommendations(vehicleId: string): Promise<Recommendation[]> {
    const out: Recommendation[] = [];
    for (const o of this.orders.values()) {
      if (o.vehicleId !== vehicleId || !this.myRole(o.workshopId)) continue;
      for (const i of this.items.values()) {
        if (i.workOrderId !== o.id || i.approval !== "declined" || this.followups.has(i.id)) continue;
        const p = this.priced(o.workshopId, i);
        out.push({
          itemId: i.id, workOrderId: o.id, workOrderNumber: o.number, declinedOn: o.closedAt ?? o.receivedAt, gross: p.gross,
          line: { kind: i.kind, description: i.description, partNumber: i.partNumber ?? null, unit: i.unit ?? null, quantity: i.quantity, unitPrice: p.unitPrice, priceMode: i.priceMode, vatRate: i.vatRate },
        });
      }
    }
    return out.sort((a, b) => b.declinedOn.localeCompare(a.declinedOn));
  }
  async closeRecommendation(itemId: string, workOrderId: string | null) {
    const i = this.items.get(itemId);
    const o = i ? this.orders.get(i.workOrderId) : undefined;
    if (!i || !o || i.approval !== "declined" || !this.can(o.workshopId, "items")) throw new DomainError("NOT_ALLOWED");
    if (workOrderId) {
      const w = this.orders.get(workOrderId);
      if (!w || w.vehicleId !== o.vehicleId || w.status === "closed") throw new DomainError("NOT_ALLOWED");
    }
    if (!this.followups.has(itemId)) this.followups.set(itemId, workOrderId);
  }
  // ---- the mechanic's proposals (like public.work_order_proposals) ----
  private proposals: (Proposal & { workshopId: string; status: "open" | "taken" | "dropped"; itemId: string | null })[] = [];
  async vehicleProposals(vehicleId: string): Promise<Proposal[]> {
    return this.proposals
      .filter((p) => p.status === "open" && this.orders.get(p.workOrderId)?.vehicleId === vehicleId && this.myRole(p.workshopId))
      .map((p) => {
        const o = this.orders.get(p.workOrderId)!;
        const { workshopId: _w, status: _s, itemId: _i, ...out } = p;
        return { ...out, workOrderNumber: o.number, workOrderStatus: o.status };
      })
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  private cleanProposal(input: ProposalInput) {
    const description = input.description.trim();
    if (!description || description.length > 500) throw new DomainError("REQUIRED_MISSING");
    if (input.quantity != null && !(input.quantity > 0)) throw new DomainError("INVALID_QUANTITY");
    return { kind: input.kind, description, partNumber: input.partNumber?.trim() || null, quantity: input.quantity ?? null, unit: input.unit?.trim() || null };
  }
  async addProposal(workshopId: string, workOrderId: string, input: ProposalInput) {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    this.assertWritable(workshopId);
    const o = this.requireOpen(workOrderId);
    if (o.workshopId !== workshopId) throw new DomainError("NOT_ALLOWED");
    this.proposals.push({ ...this.cleanProposal(input), id: newId(), workshopId, workOrderId, workOrderNumber: o.number, workOrderStatus: o.status,
      proposedBy: this.user?.id ?? null, createdAt: new Date(Date.now() + this.proposals.length).toISOString(), status: "open", itemId: null });
  }
  private ownProposal(id: string) {
    const p = this.proposals.find((x) => x.id === id);
    if (!p || !this.myRole(p.workshopId)) throw new DomainError("NOT_ALLOWED");
    const mine = p.proposedBy === this.user?.id && p.status === "open";
    return { p, mine };
  }
  async updateProposal(id: string, input: ProposalInput) {
    const { p, mine } = this.ownProposal(id);
    if (!mine && !this.can(p.workshopId, "items")) throw new DomainError("NOT_ALLOWED");
    Object.assign(p, this.cleanProposal(input));
  }
  async deleteProposal(id: string) {
    const { p, mine } = this.ownProposal(id);
    if (!mine && !this.can(p.workshopId, "items")) throw new DomainError("NOT_ALLOWED");
    this.proposals = this.proposals.filter((x) => x.id !== id);
  }
  async closeProposal(id: string, itemId: string | null) {
    const { p } = this.ownProposal(id);
    this.requireCan(p.workshopId, "items");
    p.status = itemId ? "taken" : "dropped";
    p.itemId = itemId;
  }
  async sendDeclinedWork(workOrderId: string, message: { subject: string; text: string }) {
    const o = this.orders.get(workOrderId);
    const c = o?.customerId ? this.customers.get(o.customerId) : undefined;
    if (!o || !c || !this.can(o.workshopId, "notice")) throw new DomainError("NOT_ALLOWED");
    const w = this.workshops.find((x) => x.id === o.workshopId);
    if (!w || !featureOn(w, "declinedWorkEmail")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (this.notices.has(workOrderId)) throw new DomainError("SEND_FAILED", "already sent");
    if (![...this.items.values()].some((i) => i.workOrderId === workOrderId && i.approval === "declined")) throw new DomainError("NOT_ALLOWED");
    if (!c.email) throw new DomainError("SEND_FAILED");
    this.notices.set(workOrderId, now());
    this.sentMessages.push({ workshopId: o.workshopId, workOrderId, channel: "email", to: c.email, price: 0, at: now(), subject: message.subject, text: message.text, link: "", buttonLabel: "" });
    return { to: c.email, price: 0 };
  }
  async declinedWorkSent(workOrderId: string) {
    return this.notices.get(workOrderId) ?? null;
  }
  async createQuoteRequest(workOrderId: string) {
    const { o, q } = this.startQuote(workOrderId, "link", null);
    this.log(o, "quote_sent");
    return q.token;
  }
  async recordQuoteAnswer(workOrderId: string, approvedIds: string[], note?: string) {
    const { q } = this.startQuote(workOrderId, "phone", note?.trim() || null);
    this.applyAnswer(q, approvedIds);
  }
  private quoteByToken(token: string) {
    const q = this.quotes.find((x) => x.token === token && x.channel === "link");
    if (!q) throw new DomainError("QUOTE_NOT_FOUND");
    return q;
  }
  async quoteView(token: string): Promise<PublicQuote> {
    const q = this.quoteByToken(token);
    if (!q.answeredAt && q.expiresAt <= now()) throw new DomainError("QUOTE_EXPIRED");
    const o = this.orders.get(q.workOrderId)!;
    const w = this.workshops.find((x) => x.id === o.workshopId)!;
    const v = this.vehicles.get(o.vehicleId ?? "");
    const g = (k: string) => (typeof w.general[k] === "string" && String(w.general[k]).trim()) || null;
    return {
      workshopName: w.name, customerName: this.customers.get(o.customerId ?? "")?.name ?? null, workshopPhone: g("workshopPhone"), workshopAddress: g("workshopAddress"), contacts: workshopContactsOf(w.general), language: w.language, currency: w.currency,
      number: o.number, plateNumber: v?.plateNumber ?? null, plateCountry: v?.plateCountry ?? null,
      vehicle: v ? [v.make, v.model].filter(Boolean).join(" ") || null : null,
      findings: o.findings?.trim() || null,
      answeredAt: q.answeredAt, approvedIds: [...q.approvedIds], expiresAt: q.expiresAt,
      items: q.itemIds.map((id) => this.items.get(id)).filter((i): i is WorkOrderItem => !!i)
        .map((i) => ({ id: i.id, kind: i.kind, description: i.description, quantity: i.quantity, unit: i.unit ?? null, gross: i.gross, optional: i.optional ?? false })),
    };
  }
  async quoteAnswer(token: string, approvedIds: string[]) {
    const q = this.quoteByToken(token);
    if (q.answeredAt) throw new DomainError("QUOTE_ANSWERED");
    if (q.expiresAt <= now()) throw new DomainError("QUOTE_EXPIRED");
    if (this.orders.get(q.workOrderId)?.status === "closed") throw new DomainError("WORK_ORDER_CLOSED");
    this.applyAnswer(q, approvedIds);
  }
  async recentQuoteAnswers(workshopId: string, sinceIso: string): Promise<QuoteNotice[]> {
    return this.quotes
      .filter((q) => q.channel === "link" && q.answeredAt && q.answeredAt > sinceIso && this.orders.get(q.workOrderId)?.workshopId === workshopId)
      .sort((a, b) => b.answeredAt!.localeCompare(a.answeredAt!))
      .map((q) => {
        const o = this.decorate(this.orders.get(q.workOrderId)!);
        return { quoteId: q.id, workOrderId: o.id, number: o.number, plateNumber: o.plateNumber ?? null, plateCountry: o.plateCountry ?? null, customerName: o.customerName ?? null,
          answeredAt: q.answeredAt!, approved: q.approvedIds.length, total: q.itemIds.length };
      });
  }
  async deleteWorkOrderItem(id: string) {
    const i = this.items.get(id);
    if (!i) return;
    const o = this.requireOpen(i.workOrderId);
    this.requireCan(o.workshopId, "items");
    this.items.delete(id);
    this.syncItemStock(id, null);
  }
  async itemSuggestions(workshopId: string, text: string, limit = 8): Promise<ItemSuggestion[]> {
    const q = text.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set<string>();
    const out: ItemSuggestion[] = [];
    const mine = new Set([...this.orders.values()].filter((o) => o.workshopId === workshopId).map((o) => o.id));
    for (const i of [...this.items.values()].reverse()) {
      if (!mine.has(i.workOrderId) || !(i.description.toLowerCase().includes(q) || (i.partNumber ?? "").toLowerCase().includes(q))) continue;
      const k = i.description.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ kind: i.kind, description: i.description, partNumber: i.partNumber, unit: i.unit, quantity: i.quantity, unitPrice: this.priced(workshopId, i).unitPrice, priceMode: i.priceMode, vatRate: i.vatRate });
      if (out.length >= limit) break;
    }
    return out;
  }
  // --- article master and stock (the database does the same with triggers) ---
  private articles = new Map<string, Article>();
  private articleNumbers = new Map<string, ArticleNumberRow & { articleId: string }>();
  private movements: (StockMovement & { workshopId: string; itemId: string | null })[] = [];
  private moveSeq = 0;

  private applyMovement(articleId: string, quantity: number) {
    const a = this.articles.get(articleId);
    if (a) this.articles.set(articleId, { ...a, stock: Math.round((a.stock + quantity) * 1000) / 1000 });
  }
  /** Keeps the work order movement of a line in step with the line. */
  private syncItemStock(itemId: string, item: WorkOrderItem | null) {
    const old = this.movements.find((m) => m.itemId === itemId);
    if (old) {
      this.applyMovement(old.articleId, -old.quantity);
      this.movements = this.movements.filter((m) => m !== old);
    }
    const a = item?.articleId ? this.articles.get(item.articleId) : undefined;
    if (!item || !a || !a.trackStock || item.approval === "declined" || item.customerSupplied) return;
    const order = this.orders.get(item.workOrderId);
    this.movements.push({ id: String(++this.moveSeq), workshopId: a.workshopId, itemId, articleId: a.id, quantity: -item.quantity, kind: "work_order", unitCost: null,
      supplier: null, documentNo: null, note: null, workOrderNumber: order?.number ?? null, createdAt: new Date(Date.now() + this.moveSeq).toISOString() });
    this.applyMovement(a.id, -item.quantity);
  }
  async searchArticles(workshopId: string, query: string, limit = 50) {
    const q = query.trim().toLowerCase();
    const key = partSearchKey(query);
    const cross = new Set([...this.articleNumbers.values()].filter((n) => key && partSearchKey(n.number).startsWith(key)).map((n) => n.articleId));
    return [...this.articles.values()]
      .filter((a) => a.workshopId === workshopId && a.active !== false)
      .filter((a) => !q || (key && partSearchKey(a.partNumber ?? "").startsWith(key)) || a.name.toLowerCase().includes(q) || cross.has(a.id))
      .sort((a, b) => b.usageCount - a.usageCount || a.name.localeCompare(b.name))
      .slice(0, limit)
      .map((a) => ({ ...a }));
  }
  async getArticle(id: string) {
    const a = this.articles.get(id);
    if (!a) return null;
    const numbers = [...this.articleNumbers.values()].filter((n) => n.articleId === id).map(({ articleId: _a, ...n }) => n);
    return { ...a, numbers };
  }
  async saveArticle(workshopId: string, input: ArticleInput, id?: string) {
    this.assertWritable(workshopId);
    this.requireCan(workshopId, "stock");
    const v = validateArticle(input);
    const old = id ? this.articles.get(id) : undefined;
    const a: Article = { ...v, salePriceMode: v.salePriceMode ?? "gross", vatRate: v.vatRate ?? 27, trackStock: v.trackStock ?? false, bulk: v.bulk ?? false, active: v.active ?? true,
      id: old?.id ?? newId(), workshopId, stock: old?.stock ?? 0, usageCount: old?.usageCount ?? 0, lastUsed: old?.lastUsed ?? null, legacyId: old?.legacyId ?? null };
    delete a.numbers;
    this.articles.set(a.id, a);
    return (await this.getArticle(a.id))!;
  }
  async deleteArticle(id: string) {
    const a = this.articles.get(id);
    if (!a) return;
    const role = this.myRole(a.workshopId);
    if (role !== "owner" && role !== "manager") throw new DomainError("NOT_ALLOWED");
    if ([...this.items.values()].some((i) => i.articleId === id)) throw new DomainError("ARTICLE_IN_USE");
    this.articles.delete(id);
    this.movements = this.movements.filter((m) => m.articleId !== id);
  }
  async addArticleNumber(workshopId: string, articleId: string, n: ArticleNumber) {
    this.requireCan(workshopId, "stock");
    const row = { id: newId(), articleId, number: n.number.trim().toUpperCase(), kind: n.kind, brand: n.brand ?? null };
    this.articleNumbers.set(row.id, row);
    const { articleId: _a, ...out } = row;
    return out;
  }
  async deleteArticleNumber(id: string) {
    this.articleNumbers.delete(id);
  }
  async stockMovements(articleId: string, limit = 100) {
    const a = this.articles.get(articleId);
    if (!a || !this.can(a.workshopId, "stock")) return [];
    return this.movements.filter((m) => m.articleId === articleId).sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, limit)
      .map(({ workshopId: _w, itemId: _i, ...m }) => m);
  }
  async addStockMovement(workshopId: string, m: NewStockMovement) {
    this.assertWritable(workshopId);
    this.requireCan(workshopId, "stock");
    if (!Number.isFinite(m.quantity) || m.quantity === 0) throw new DomainError("INVALID_STOCK_QUANTITY");
    this.movements.push({ id: String(++this.moveSeq), workshopId, itemId: null, articleId: m.articleId, quantity: m.quantity, kind: m.kind, unitCost: m.unitCost ?? null,
      supplier: m.supplier?.trim() || null, documentNo: m.documentNo?.trim() || null, note: m.note?.trim() || null, workOrderNumber: null, createdAt: new Date(Date.now() + this.moveSeq).toISOString() });
    this.applyMovement(m.articleId, m.quantity);
  }
  async importLegacyArticles(workshopId: string, articles: LegacyArticle[]) {
    const role = this.myRole(workshopId);
    if (role !== "owner" && role !== "manager") throw new DomainError("NOT_ALLOWED");
    const byLegacy = new Map([...this.articles.values()].filter((a) => a.workshopId === workshopId && a.legacyId).map((a) => [a.legacyId, a]));
    for (const l of articles) {
      const old = byLegacy.get(l.legacyId);
      if (old) {
        this.articles.set(old.id, { ...old, salePrice: l.salePrice, usageCount: l.usageCount, lastUsed: l.lastUsed });
        continue;
      }
      const a: Article = { id: newId(), workshopId, legacyId: l.legacyId, partNumber: l.partNumber, name: l.name, category: l.category, unit: l.unit, salePrice: l.salePrice,
        salePriceMode: "gross", vatRate: l.vatRate, trackStock: false, bulk: false, active: true, stock: 0, usageCount: l.usageCount, lastUsed: l.lastUsed, minStock: null };
      this.articles.set(a.id, a);
    }
    return articles.length;
  }

  private photos = new Map<string, Photo>();
  private photoBlobs = new Map<string, Blob>();
  async listPhotos(filter: { workOrderId?: string; vehicleId?: string }) {
    return [...this.photos.values()]
      .filter((p) => (filter.workOrderId ? p.workOrderId === filter.workOrderId : true) && (filter.vehicleId ? p.vehicleId === filter.vehicleId : true))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  async addPhoto(workshopId: string, photo: NewPhoto, image: { blob: Blob; width: number; height: number }) {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    await this.storageStepUp();
    // the photo of the signed paper receipt: a document of the work order, also after closing
    if (photo.docType && (photo.docType !== "signedIntake" || photo.kind !== "document" || !photo.workOrderId)) throw new DomainError("NOT_ALLOWED");
    if (photo.workOrderId && !photo.docType) this.requireOpen(photo.workOrderId);
    // like public.photos_intake_guard: an intake photo has its work order and view, and needs the feature
    if (photo.kind === "intake") {
      if (!photo.workOrderId || !photo.intakeView) throw new DomainError("REQUIRED_MISSING");
      const w = this.workshops.find((x) => x.id === workshopId);
      if (!w || !featureOn(w, "intakePhotos")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    }
    const vehicleId = photo.vehicleId ?? (photo.workOrderId ? this.orders.get(photo.workOrderId)?.vehicleId ?? null : null);
    // intake photos as data URLs: the customer's page (another tab of the browser tests) shows them too
    const url = photo.kind === "intake" ? await blobDataUrl(image.blob, "image/jpeg") : typeof URL.createObjectURL === "function" ? URL.createObjectURL(image.blob) : "";
    const p: Photo = { id: newId(), workshopId, vehicleId, workOrderId: photo.workOrderId, kind: photo.kind, url, width: image.width, height: image.height,
      caption: photo.caption ?? null, takenBy: this.user?.id ?? null, createdAt: new Date(Date.now() + this.photos.size).toISOString(),
      intakeView: photo.kind === "intake" ? photo.intakeView ?? null : null, docType: photo.docType ?? null };
    this.photos.set(p.id, p);
    this.photoBlobs.set(p.id, image.blob);
    return { ...p };
  }
  /** Logo images by storage path, as data URLs (the kept demo store saves them as text). */
  private logoData = new Map<string, string>();
  private lastLogoVersion = "";
  async saveLogo(workshopId: string, image: PreparedLogo): Promise<WorkshopLogo> {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    this.assertWritable(workshopId);
    // like the bucket: PNG or JPEG, at most 300 KB
    if (image.type !== "image/png" && image.type !== "image/jpeg") throw new DomainError("LOGO_TYPE");
    if (image.blob.size > LOGO_MAX_BYTES) throw new DomainError("LOGO_TOO_LARGE");
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w) throw new DomainError("NOT_ALLOWED");
    let version = newLogoVersion();
    // two uploads within the same millisecond (tests) still get different files
    if (version <= this.lastLogoVersion) version = (parseInt(this.lastLogoVersion, 36) + 1).toString(36);
    this.lastLogoVersion = version;
    const path = logoPath(workshopId, version, image.type);
    this.logoData.set(path, await blobDataUrl(image.blob, image.type));
    if (w.logo) this.logoData.delete(w.logo.path);
    w.logo = { path, version, width: image.width, height: image.height };
    return { ...w.logo };
  }
  async removeLogo(workshopId: string) {
    if (this.myRole(workshopId) !== "owner") throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    this.assertWritable(workshopId);
    const w = this.workshops.find((x) => x.id === workshopId);
    if (w?.logo) this.logoData.delete(w.logo.path);
    if (w) w.logo = null;
  }
  async logoImageUrl(workshopId: string, logo: WorkshopLogo) {
    if (!this.myRole(workshopId)) return null;
    return this.logoData.get(logo.path) ?? null;
  }
  async quoteLogoUrl(token: string) {
    const q = this.quoteByToken(token);
    if (!q.answeredAt && q.expiresAt <= now()) throw new DomainError("QUOTE_EXPIRED");
    const o = this.orders.get(q.workOrderId)!;
    const w = this.workshops.find((x) => x.id === o.workshopId);
    return w?.logo ? this.logoData.get(w.logo.path) ?? null : null;
  }
  async deletePhoto(id: string) {
    const p = this.photos.get(id);
    if (!p) return;
    if (p.workOrderId) this.requireOpen(p.workOrderId);
    const role = this.myRole(p.workshopId);
    if (p.takenBy !== this.user?.id && role !== "owner" && role !== "manager") throw new DomainError("NOT_ALLOWED");
    this.photos.delete(id);
  }

  // --- intake with photos and the signature on the phone (the database: 20261217000000_intake_photos_sign.sql) ---
  private signatureRows: WorkOrderSignature[] = [];
  private intakeLinks: (IntakeLink & { id: string; workOrderId: string })[] = [];
  private objections: (IntakeObjection & { linkId: string })[] = [];
  async signatures(workOrderId: string) {
    const o = this.orders.get(workOrderId);
    if (!o || !this.myRole(o.workshopId)) return [];
    return this.signatureRows.filter((s) => s.workOrderId === workOrderId).map((s) => structuredClone(s));
  }
  async addSignature(workshopId: string, workOrderId: string, s: NewSignature) {
    const o = this.orders.get(workOrderId);
    if (!o || o.workshopId !== workshopId || !this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    this.assertWritable(workshopId);
    const w = this.workshops.find((x) => x.id === workshopId);
    if (!w || !featureOn(w, "phoneSignature")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (s.kind === "intake" && o.status === "closed") throw new DomainError("WORK_ORDER_CLOSED");
    if (s.kind === "intake" && intakePending(o)) throw new DomainError("INTAKE_PENDING");
    if (s.kind === "handover" && o.status !== "closed") throw new DomainError("WORK_ORDER_NOT_CLOSED");
    if (this.signatureRows.some((x) => x.workOrderId === workOrderId && x.kind === s.kind)) throw new DomainError("SIGNATURE_EXISTS");
    if (!/^[0-9a-f]{64}$/.test(s.contentHash) || !s.signerName.trim()) throw new DomainError("REQUIRED_MISSING");
    const row: WorkOrderSignature = { id: newId(), workOrderId, kind: s.kind, signerName: s.signerName.trim(), signedAt: now(), contentHash: s.contentHash,
      content: structuredClone(s.content), imageUrl: await blobDataUrl(s.image, "image/png") };
    this.signatureRows.push(row);
    return structuredClone(row);
  }
  async createIntakeLink(workOrderId: string) {
    const o = this.orders.get(workOrderId);
    if (!o || !this.myRole(o.workshopId)) throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    this.assertWritable(o.workshopId);
    const w = this.workshops.find((x) => x.id === o.workshopId);
    if (!w || !featureOn(w, "intakePhotos")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    for (const l of this.intakeLinks) if (l.workOrderId === workOrderId && l.expiresAt > now()) l.expiresAt = now();
    const token = Array.from({ length: 64 }, () => "0123456789abcdef"[Math.floor(Math.random() * 16)]).join("");
    this.intakeLinks.push({ id: newId(), workOrderId, token, createdAt: now(), expiresAt: new Date(Date.now() + 14 * 86400000).toISOString(), sentTo: null, sentAt: null });
    return token;
  }
  async lastIntakeLink(workOrderId: string): Promise<IntakeLink | null> {
    const o = this.orders.get(workOrderId);
    if (!o || !this.myRole(o.workshopId)) return null;
    const l = [...this.intakeLinks].reverse().find((x) => x.workOrderId === workOrderId);
    return l ? { token: l.token, createdAt: l.createdAt, expiresAt: l.expiresAt, sentTo: l.sentTo, sentAt: l.sentAt } : null;
  }
  async sendIntakeEmail(workOrderId: string, message: QuoteMessage) {
    const o = this.orders.get(workOrderId);
    const c = o?.customerId ? this.customers.get(o.customerId) : undefined;
    if (!o || !this.myRole(o.workshopId)) throw new DomainError("NOT_ALLOWED");
    const w = this.workshops.find((x) => x.id === o.workshopId);
    if (!w || !featureOn(w, "intakePhotos")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (w.general.intakePhotoSend === "never") throw new DomainError("NOT_ALLOWED");
    const l = [...this.intakeLinks].reverse().find((x) => x.workOrderId === workOrderId);
    if (!l || l.expiresAt <= now() || !message.link.endsWith(`#intake/${l.token}`)) throw new DomainError("INTAKE_NOT_FOUND");
    if (!message.text.includes(message.link)) throw new DomainError("NOT_ALLOWED");
    // the mechanic sends it once at the intake; sending again is the administrator's
    if (this.intakeLinks.some((x) => x.workOrderId === workOrderId && x.sentAt) && !this.can(o.workshopId, "notice")) throw new DomainError("NOT_ALLOWED");
    if (!c?.email) throw new DomainError("SEND_FAILED");
    l.sentTo = c.email;
    l.sentAt = now();
    this.sentMessages.push({ workshopId: o.workshopId, workOrderId, channel: "email", to: c.email, price: 0, at: now(), ...message });
    return { to: c.email, price: 0 };
  }
  async sendIntakeSms(workOrderId: string, message: QuoteMessage) {
    const o = this.orders.get(workOrderId);
    const c = o?.customerId ? this.customers.get(o.customerId) : undefined;
    if (!o || !this.myRole(o.workshopId)) throw new DomainError("NOT_ALLOWED");
    const w = this.workshops.find((x) => x.id === o.workshopId);
    if (!w || !featureOn(w, "intakePhotos")) throw new DomainError("FEATURE_NOT_IN_PLAN");
    if (w.general.intakePhotoSend === "never") throw new DomainError("NOT_ALLOWED");
    this.smsReady(o.workshopId);
    const l = [...this.intakeLinks].reverse().find((x) => x.workOrderId === workOrderId);
    if (!l || l.expiresAt <= now() || !message.link.endsWith(`#intake/${l.token}`)) throw new DomainError("INTAKE_NOT_FOUND");
    if (!message.text.includes(message.link)) throw new DomainError("NOT_ALLOWED");
    if (this.intakeLinks.some((x) => x.workOrderId === workOrderId && x.sentAt) && !this.can(o.workshopId, "notice")) throw new DomainError("NOT_ALLOWED");
    const to = c?.mobile || c?.phone;
    if (!to) throw new DomainError("SEND_FAILED");
    const price = this.smsPrice(o.workshopId);
    l.sentTo = l.sentTo ?? `…${to.replace(/\D/g, "").slice(-4)}`;
    l.sentAt = now();
    this.sentMessages.push({ workshopId: o.workshopId, workOrderId, channel: "sms", to, price, at: now(), ...message });
    return { to, price };
  }
  private intakeLinkOf(token: string) {
    const l = this.intakeLinks.find((x) => x.token === token);
    if (!l) throw new DomainError("INTAKE_NOT_FOUND");
    if (l.expiresAt <= now()) throw new DomainError("INTAKE_EXPIRED");
    return l;
  }
  async intakeView(token: string): Promise<PublicIntake> {
    const l = this.intakeLinkOf(token);
    const o = this.orders.get(l.workOrderId)!;
    const w = this.workshops.find((x) => x.id === o.workshopId)!;
    const v = this.vehicles.get(o.vehicleId ?? "");
    const g = (k: string) => (typeof w.general[k] === "string" && String(w.general[k]).trim()) || null;
    const sig = this.signatureRows.find((x) => x.workOrderId === o.id && x.kind === "intake");
    const obj = this.objections.find((x) => x.linkId === l.id);
    return {
      workshopName: w.name, workshopPhone: g("workshopPhone"), workshopAddress: g("workshopAddress"), contacts: workshopContactsOf(w.general), language: w.language,
      logoUrl: w.logo ? this.logoData.get(w.logo.path) ?? null : null,
      number: o.number, plateNumber: v?.plateNumber ?? null, plateCountry: v?.plateCountry ?? null,
      vehicle: v ? [v.make, v.model].filter(Boolean).join(" ") || null : null,
      customerName: this.customers.get(o.customerId ?? "")?.name ?? null,
      odometerKm: o.odometerKm ?? null, receivedAt: o.receivedAt, expectedReadyAt: o.expectedReadyAt ?? null, customerRequest: o.customerRequest ?? null,
      intakeExtras: o.intakeExtras ?? null,
      expiresAt: l.expiresAt,
      photos: [...this.photos.values()].filter((p) => p.workOrderId === o.id && p.kind === "intake").sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        .map((p) => ({ id: p.id, view: p.intakeView ?? "damage", createdAt: p.createdAt, url: p.url })),
      signature: sig ? { signedAt: sig.signedAt, signerName: sig.signerName, contentHash: sig.contentHash, url: sig.imageUrl } : null,
      objection: obj ? { createdAt: obj.createdAt } : null,
    };
  }
  async intakeObject(token: string, text: string) {
    const l = this.intakeLinkOf(token);
    if (this.signatureRows.some((x) => x.workOrderId === l.workOrderId && x.kind === "intake")) throw new DomainError("INTAKE_SIGNED");
    if (this.objections.some((x) => x.linkId === l.id)) throw new DomainError("INTAKE_OBJECTION_SENT");
    const clean = cleanObjection(text);
    if (!clean) throw new DomainError("REQUIRED_MISSING");
    this.objections.push({ id: newId(), linkId: l.id, workOrderId: l.workOrderId, text: clean, createdAt: now(), resolvedAt: null });
  }
  async intakeObjections(workOrderId: string): Promise<IntakeObjection[]> {
    const o = this.orders.get(workOrderId);
    if (!o || !this.myRole(o.workshopId)) return [];
    return this.objections.filter((x) => x.workOrderId === workOrderId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map(({ linkId: _l, ...x }) => ({ ...x }));
  }
  async recentIntakeObjections(workshopId: string, sinceIso: string): Promise<IntakeObjectionNotice[]> {
    if (!this.myRole(workshopId)) return [];
    return this.objections
      .filter((x) => x.createdAt > sinceIso && this.orders.get(x.workOrderId)?.workshopId === workshopId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map(({ linkId: _l, ...x }) => {
        const o = this.decorate(this.orders.get(x.workOrderId)!);
        return { ...x, number: o.number, plateNumber: o.plateNumber ?? null, plateCountry: o.plateCountry ?? null, customerName: o.customerName ?? null };
      });
  }
  async resolveIntakeObjection(id: string) {
    const x = this.objections.find((y) => y.id === id);
    const o = x ? this.orders.get(x.workOrderId) : undefined;
    if (!x || !o || !this.myRole(o.workshopId)) throw new DomainError("NOT_ALLOWED");
    this.mfaAssert();
    if (!x.resolvedAt) x.resolvedAt = now();
  }

  async lastOdometer(vehicleId: string) {
    const kms = [...this.orders.values()].filter((o) => o.vehicleId === vehicleId).map((o) => o.odometerKm ?? -1);
    kms.push(this.vehicles.get(vehicleId)?.odometerKm ?? -1);
    const max = Math.max(...kms);
    return max >= 0 ? max : null;
  }

  async supplementLegacyWorkOrders(workshopId: string, rows: LegacyWorkOrderExtra[]) {
    const role = this.myRole(workshopId);
    if (role !== "owner" && role !== "manager") throw new DomainError("NOT_ALLOWED");
    const byNumber = new Map([...this.orders.values()].filter((o) => o.workshopId === workshopId && o.legacyId).map((o) => [o.number, o]));
    let n = 0;
    for (const r of rows) {
      const o = byNumber.get(r.number);
      if (!o) continue;
      const km = o.odometerKm ?? r.odometerKm;
      const notes = o.notes ?? r.notes;
      if (km === o.odometerKm && notes === (o.notes ?? null)) continue;
      this.orders.set(o.id, { ...o, odometerKm: km, notes });
      n++;
    }
    return n;
  }
  async importLegacyWorkOrders(workshopId: string, orderRows: Row[], itemRows: Row[], onProgress?: (p: ImportProgress) => void): Promise<WorkOrderImportResult> {
    const role = this.myRole(workshopId);
    if (role !== "owner" && role !== "manager") throw new DomainError("NOT_ALLOWED");
    const vehicles = [...this.vehicles.values()].filter((v) => v.workshopId === workshopId);
    const customers = [...this.customers.values()].filter((c) => c.workshopId === workshopId);
    if (vehicles.length === 0 || customers.length === 0) throw new DomainError("IMPORT_NEEDS_MASTER_DATA");
    const byPlate = new Map<string, { id: string; customerId: string | null }[]>();
    for (const v of vehicles) byPlate.set(plateSearchKey(v.plateNumber), [...(byPlate.get(plateSearchKey(v.plateNumber)) ?? []), { id: v.id, customerId: v.customerId ?? null }]);
    const plan = planLegacyWorkOrders(orderRows, itemRows, {
      vehicleByLegacyId: new Map(vehicles.filter((v) => v.legacyId).map((v) => [v.legacyId!, v.id])),
      vehiclesByPlate: byPlate,
      customerByLegacyId: new Map(customers.filter((c) => c.legacyId).map((c) => [c.legacyId!, c.id])),
      ownerOfVehicle: new Map(vehicles.map((v) => [v.id, v.customerId ?? null])),
    });
    const existing = new Set([...this.orders.values()].filter((o) => o.workshopId === workshopId && o.legacyId).map((o) => o.legacyId));
    const idByLegacy = new Map<string, string>();
    let created = 0;
    let done = 0;
    for (const o of plan.orders) {
      onProgress?.({ phase: "workOrders", done: ++done, total: plan.orders.length });
      if (existing.has(o.legacyId)) continue;
      const order: WorkOrder = { id: newId(), workshopId, number: o.number, status: "closed", vehicleId: o.vehicleId, customerId: o.customerId, odometerKm: null, receivedAt: o.receivedAt, readyAt: null, closedAt: o.receivedAt, needsReview: o.needsReview, legacyId: o.legacyId };
      this.orders.set(order.id, order);
      idByLegacy.set(o.legacyId, order.id);
      created++;
    }
    let items = 0;
    for (const i of plan.items) {
      const orderId = idByLegacy.get(i.workOrderLegacyId);
      if (!orderId) continue;
      const { workOrderLegacyId: _w, legacyId: _l, ...line } = i;
      this.items.set(newId(), { ...line, id: newId(), workOrderId: orderId });
      items++;
    }
    onProgress?.({ phase: "done", done: 0, total: 0 });
    return { workOrders: created, items, skippedOrders: plan.skippedOrders, skippedItems: plan.skippedItems, toReview: plan.toReview };
  }

  // ---- feedback loop (demo: the "GitHub issues" live here; nothing leaves the browser) ----
  /** Whether the feedback function is set up (false: like a missing GH_FEEDBACK_TOKEN). */
  feedbackOn = true;
  /** The demo developer answers every new report at once and asks for a check (off: tests answer by hand). */
  feedbackAutoAnswer = true;
  /** The "issues" (tests read them: title, labels, what was sent). */
  feedbackIssues: { workshopId: string; issueNumber: number; kind: FeedbackKind; title: string; text: string; page: string; appVersion: string;
    screenshot: boolean; createdAt: string; state: "open" | "closed"; labels: string[]; comments: FeedbackComment[] }[] = [];
  private feedbackSeq = 100;
  private feedbackGate(workshopId: string) {
    if (!this.myRole(workshopId)) throw new DomainError("NOT_ALLOWED");
    if (!this.feedbackOn) throw new DomainError("FEEDBACK_UNAVAILABLE");
  }
  /** The developer's answer on "GitHub"; askCheck puts the "ellenorizd" label on it. */
  feedbackDeveloperAnswer(issueNumber: number, body: string, askCheck = true) {
    const i = this.feedbackIssues.find((x) => x.issueNumber === issueNumber);
    if (!i) return;
    i.comments.push({ author: "garagevo-dev", fromDeveloper: true, body, createdAt: new Date(Date.now() + i.comments.length + 1).toISOString() });
    if (askCheck && !i.labels.includes(LABEL_CHECK)) i.labels.push(LABEL_CHECK);
  }
  async feedbackReport(workshopId: string, input: FeedbackInput) {
    this.feedbackGate(workshopId);
    const r = cleanReport(input);
    if ("error" in r) throw new DomainError(r.error);
    const issueNumber = ++this.feedbackSeq;
    this.feedbackIssues.push({ workshopId, issueNumber, kind: r.kind, title: r.title, text: r.text, page: r.page, appVersion: r.appVersion,
      screenshot: !!input.screenshot, createdAt: new Date(Date.now() + this.feedbackSeq).toISOString(), state: "open", labels: ["feedback", r.kind], comments: [] });
    if (this.feedbackAutoAnswer) {
      this.feedbackDeveloperAnswer(issueNumber, "Köszönöm a jelzést! Javítottam, a következő frissítésben már benne lesz. Kérem, nézze meg, és jelezze, hogy most jó-e.");
    }
    return { issueNumber };
  }
  async feedbackList(workshopId: string): Promise<FeedbackReport[]> {
    this.feedbackGate(workshopId);
    return this.feedbackIssues
      .filter((i) => i.workshopId === workshopId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, 30)
      .map((i) => ({ issueNumber: i.issueNumber, kind: i.kind, title: i.title, text: i.text, createdAt: i.createdAt, status: feedbackStatus(i.state, i.labels), comments: i.comments.map((c) => ({ ...c })) }));
  }
  async feedbackReply(workshopId: string, issueNumber: number, verdict: FeedbackVerdict, text: string) {
    this.feedbackGate(workshopId);
    const r = cleanReply(verdict, text);
    if ("error" in r) throw new DomainError(r.error);
    const i = this.feedbackIssues.find((x) => x.issueNumber === issueNumber && x.workshopId === workshopId);
    if (!i) throw new DomainError("FEEDBACK_NOT_FOUND");
    const role = this.myRole(workshopId) ?? "";
    i.comments.push({ author: "garagevo-bot", fromDeveloper: false, body: commentText(replyComment(r.verdict, roleHu(role), r.text)), createdAt: new Date(Date.now() + i.comments.length + 1).toISOString() });
    const change = verdictChanges(r.verdict);
    i.labels = [...i.labels.filter((l) => !change.remove.includes(l)), ...change.add.filter((l) => !i.labels.includes(l))];
    if (change.state) i.state = change.state;
  }
}


/** A blob as a data URL (works in the browser and in the tests' jsdom). */
async function blobDataUrl(blob: Blob, type: string): Promise<string> {
  const buffer = typeof blob.arrayBuffer === "function" ? await blob.arrayBuffer() : await new Response(blob).arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let text = "";
  for (let i = 0; i < bytes.length; i += 0x8000) text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${type};base64,${btoa(text)}`;
}
